import React, { useState, useEffect, useCallback, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import { Lead, Column, FilterState, LeadsPayload, PaginationState, CustomFieldFilter } from '@/types/leads/Table';
import {
    LeadProfileSettings,
    LeadProfileSectionKey,
    LEAD_SOURCE_SUBCATEGORY_MAP,
    CONSIDERATION_OPTIONS,
    STAGE_OF_ADMISSION_STEPS,
    OVERALL_STATUS_OPTIONS,
    COUNSELLOR_PRIORITY_OPTIONS,
    COUNSELLOR_QUALIFICATION_OPTIONS,
    DISQUALIFIED_REASONS,
    CLOSED_DEAD_REASONS,
    BRAND_AWARENESS_OPTIONS,
    ACADEMIC_YEAR_OPTIONS,
    IS_CONTACTABLE_OPTIONS,
    CURRICULUM_OPTIONS,
    GENDER_OPTIONS,
    PROFILE_FIELD_LABELS,
    PROFILE_ADDITIONAL_FILTER_ORDER,
    BLANK_FILTER_VALUE,
    BLANK_FILTER_LABEL,
    vistaColumnRank,
} from '@/types/leads/Profile';
import { optimizedApi } from '@/utils/api/optimized-api';
import { bulkAssignLeads } from '@/actions/lead-manager';
import { showToast } from '@/components/ui/Toast';
import { apiRequest } from '@/utils/api-request';
import ColumnsService from '@/utils/columnsCache';
import Button from '@/components/ui/Button';
import FSelect from '@/components/ui/FSelect';
import { RESPONSE_TIME_OPTIONS, formatResponseTime } from '@/utils/responseTime';
import { getCurrentUserRole, ROLES } from '@/utils/permissions';
import PipelineSearch from '@/components/PipelineTable/PipelineSearch';
import PipelineFiltersEnhanced from '@/components/PipelineTable/PipelineFiltersEnhanced';
import AddLeadModal from '@/components/PipelineTable/AddLeadModal';
import {
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Filter,
    Plus,
    Download,
    X,
    Trash2,
    UserCheck,
    Bell,
    Users,
    ChevronDown,
    Check,
    Calendar,
    Copy,
    Phone,
    PhoneCall,
    Bot,
    Loader2
} from 'lucide-react';
import ColumnVisibilityDropdown, { VISTA_UNUSED_COLUMNS } from './ColumnVisibilityDropdown';
import { format, parseISO } from 'date-fns';
import { cn } from '@/utils/cn';
import ExportModalWithFieldSelection from '@/components/ExportModalWithFieldSelection';
import LeadViewSwitcher from '@/components/LeadViewSwitcher';
import InitiateCallModal from '@/components/OutboundCalling/InitiateCallModal';
import { fetchOutboundConfig, OutboundCallConfig } from '@/utils/outbound-calling';
import { useCallingState } from '@/contexts/CallingContext';
import { useUsers } from '@/contexts/UsersContext';
import { formatPhoneDisplay, formatPhoneForTel } from '@/utils/phoneFormatter';

interface EnhancedLeadsTableProps {
    onRowSelect?: (leadIds: string[]) => void;
    onLeadClick?: (lead: Lead) => void;
    onAttachSchool?: (lead: Lead) => void; // Callback when "Attach School" is clicked (admin view)
    tourButton?: React.ReactNode;
    apiEndpoint?: string; // Custom API endpoint (default: '/leads')
    pageTitle?: string; // Custom page title
    isAdminView?: boolean; // Admin view shows leads from all clients
}

export interface EnhancedLeadsTableRef {
    refreshLeads: () => Promise<void>;
}

const EnhancedLeadsTable = forwardRef<EnhancedLeadsTableRef, EnhancedLeadsTableProps>(({
    onRowSelect,
    onLeadClick,
    onAttachSchool,
    tourButton,
    apiEndpoint = '/leads',
    pageTitle = 'Leads',
    isAdminView = false
}, ref) => {
    // State management
    const [leads, setLeads] = useState<Lead[]>([]);
    const [columns, setColumns] = useState<Column[]>([]);
    const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set());
    const [pagination, setPagination] = useState<PaginationState>({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 25 // Changed from 15 to 25
    });
    const [activeFilters, setActiveFilters] = useState<FilterState>({});
    const [tempFilters, setTempFilters] = useState<FilterState>({}); // For inputs before applying
    const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
    const [selectAll, setSelectAll] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [sortColumn, setSortColumn] = useState<string>('latest_incoming_date');
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC');
    const [isBulkActionsOpen, setIsBulkActionsOpen] = useState(false);
    const [isChangeOwnerModalOpen, setIsChangeOwnerModalOpen] = useState(false);
    const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState(false);
    const [isBulkReminderModalOpen, setIsBulkReminderModalOpen] = useState(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [selectedNewOwner, setSelectedNewOwner] = useState<string>('');
    const [ownerChangeRemark, setOwnerChangeRemark] = useState<string>('');
    const [isProcessingBulkAction, setIsProcessingBulkAction] = useState(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false);
    const [isAddLeadModalOpen, setIsAddLeadModalOpen] = useState(false);

    // Outbound calling state
    const [outboundConfig, setOutboundConfig] = useState<OutboundCallConfig | null>(null);
    const [isCallModalOpen, setIsCallModalOpen] = useState(false);

    // Runo click-to-call state
    const [runoActive, setRunoActive] = useState(false);
    const [runoUserEnabled, setRunoUserEnabled] = useState(false);
    const [runoCallingLeadId, setRunoCallingLeadId] = useState<string | null>(null);
    const [selectedCallLead, setSelectedCallLead] = useState<{ uuid: string; name: string; phone: string; country_code?: string | null } | null>(null);
    const { isCallInProgress, setCallInProgress } = useCallingState();

    // Use shared users context to avoid duplicate API calls
    const { users: usersFromContext } = useUsers();
    const users = usersFromContext as any[];

    // Filter popover state
    const [filterPopoverColumn, setFilterPopoverColumn] = useState<string | null>(null);
    const [filterPopoverPosition, setFilterPopoverPosition] = useState<{ top: number; left: number } | null>(null);

    // Bulk reminder states
    const [reminderTitle, setReminderTitle] = useState<string>('');
    const [reminderDateTime, setReminderDateTime] = useState<string>('');
    const [reminderAlertBefore, setReminderAlertBefore] = useState<string>('15 min');

    // Refs for scroll handling
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLTableSectionElement>(null);

    // Check if current user is a sales rep (hide owner filter for sales reps)
    const currentUserRole = getCurrentUserRole();
    const isSalesRep = currentUserRole === ROLES.SALES_REP ||
                       currentUserRole === ROLES.SENIOR_SALES_REP ||
                       currentUserRole === ROLES.JUNIOR_SALES_REP;
    const [columnsLoaded, setColumnsLoaded] = useState(false);

    // Persistent storage keys - separate for admin and normal views
    const USER_COLUMN_PREFERENCES_KEY = isAdminView
        ? 'crm_admin_column_preferences'
        : 'crm_user_column_preferences';

    // Helper functions for persistent storage
    const saveColumnPreferencesToStorage = (visibleCols: Set<string>) => {
        try {
            const preferences = {
                visibleColumns: Array.from(visibleCols),
                timestamp: Date.now()
            };
            localStorage.setItem(USER_COLUMN_PREFERENCES_KEY, JSON.stringify(preferences));
            console.log('💾 Column preferences saved to localStorage');
        } catch (error) {
            console.error('❌ Error saving column preferences to localStorage:', error);
        }
    };

    const loadColumnPreferencesFromStorage = (): Set<string> | null => {
        try {
            const stored = localStorage.getItem(USER_COLUMN_PREFERENCES_KEY);
            if (!stored) return null;

            const preferences = JSON.parse(stored);
            console.log('📥 Column preferences loaded from localStorage');
            return new Set(preferences.visibleColumns);
        } catch (error) {
            console.error('❌ Error loading column preferences from localStorage:', error);
            return null;
        }
    };

    // No longer need the mutation hook

    // Load outbound config on mount (from cache or API with deduplication)
    useEffect(() => {
        const loadOutboundConfig = async () => {
            const config = await fetchOutboundConfig(() =>
                apiRequest('/api/outbound-call/config', { method: 'GET' })
            );
            setOutboundConfig(config);
        };
        loadOutboundConfig();
    }, []);

    // Load Runo config (only relevant for admin view)
    useEffect(() => {
        if (!isAdminView) return;
        const loadRunoConfig = async () => {
            try {
                const [configResponse, meResponse] = await Promise.all([
                    apiRequest('/api/runo/config', { method: 'GET' }),
                    apiRequest('/users/me', { method: 'GET' }),
                ]);
                if (configResponse.success && configResponse.data?.is_active) {
                    setRunoActive(true);
                }
                const phone = meResponse?.data?.data?.phone;
                if (phone && phone !== '0000000000') {
                    setRunoUserEnabled(true);
                }
            } catch {
                // Runo not configured — silently skip
            }
        };
        loadRunoConfig();
    }, [isAdminView]);

    // Step 1: Fetch columns on mount
    useEffect(() => {
        const fetchColumns = async () => {
            try {
                console.log('🔄 Step 1: Fetching columns...');
                const response = await ColumnsService.getUserColumns();
                console.log('📥 Columns response:', response, response.fromCache ? '(from cache)' : '(from API)');
                
                if (response?.success && Array.isArray(response.columns)) {
                    const cols = response.columns;
                    console.log('✅ API returned:', cols.length, 'columns');

                    // ADMIN VIEW - Simplified column order (same as regular leads, just adds clientName)
                    if (isAdminView) {
                        console.log('🔧 Admin view - using simplified column order');

                        // Fetch admin tracking options for select fields
                        type AdminTrackingOptionsType = Record<string, { col_id: number; col_uuid: string; col_label: string; options: any[] }>;
                        type ColumnOption = { opt_id: number; opt_name: string; opt_value?: string; uuid?: string };
                        let adminTrackingOptions: AdminTrackingOptionsType = {};
                        let citiesOptions: ColumnOption[] = [];
                        let areasOptions: ColumnOption[] = [];

                        try {
                            console.log('🔄 Fetching admin tracking options...');
                            const adminOptionsResponse = await optimizedApi.get('/leads/admin-tracking/options');
                            if (adminOptionsResponse.success && adminOptionsResponse.data) {
                                adminTrackingOptions = adminOptionsResponse.data as AdminTrackingOptionsType;
                                console.log('✅ Admin tracking options loaded:', Object.keys(adminTrackingOptions));
                            }
                        } catch (error) {
                            console.error('❌ Error fetching admin tracking options:', error);
                        }

                        // Define admin columns in order (grade is now a primary field in crm_leads)
                        // Note: 'status' (Client Status) filter is disabled in admin view - handled in renderFilterInput
                        const adminColumnsOrder: Column[] = [
                            { col_name: 'phone', col_label: 'Phone', col_type: 'text', is_custom: false, col_active: 1 },
                            { col_name: 'clientName', col_label: 'School Name', col_type: 'text', is_custom: false, col_active: 1 },
                            { col_name: 'schoolCity', col_label: 'City', col_type: 'select', is_custom: false, col_active: 1, options: [] },
                            { col_name: 'schoolArea', col_label: 'Area', col_type: 'select', is_custom: false, col_active: 1, options: [] },
                            { col_name: 'name', col_label: 'Name', col_type: 'text', is_custom: false, col_active: 1 },
                            { col_name: 'email', col_label: 'Email', col_type: 'text', is_custom: false, col_active: 1 },
                            { col_name: 'admin_source', col_label: 'Admin Source', col_type: 'select', is_custom: false, col_active: 1 },
                            { col_name: 'admin_sub_source', col_label: 'Admin Sub Source', col_type: 'select', is_custom: false, col_active: 1 },
                            { col_name: 'status', col_label: 'Client Status', col_type: 'select', is_custom: false, col_active: 1 },
                            { col_name: 'admin_status', col_label: 'Admin Status', col_type: 'select', is_custom: false, col_active: 1 },
                            { col_name: 'grade', col_label: 'Grade', col_type: 'select', is_custom: false, col_active: 1 },
                            { col_name: 'last_remark', col_label: 'Client Last Comment', col_type: 'text', is_custom: false, col_active: 1 },
                            { col_name: 'admin_last_remark', col_label: 'Admin Last Comment', col_type: 'text', is_custom: false, col_active: 1 },
                            { col_name: 'admin_is_qualified', col_label: 'Admin Is Qualified', col_type: 'boolean', is_custom: false, col_active: 1 },
                            { col_name: 'admin_prospect', col_label: 'Admin Prospect', col_type: 'text', is_custom: false, col_active: 1 },
                            { col_name: 'aiProspectStatus', col_label: 'AI Prospect', col_type: 'text', is_custom: false, col_active: 1 },
                            { col_name: 'admin_near_by_client', col_label: 'Admin Near By Client', col_type: 'text', is_custom: false, col_active: 1 },
                            { col_name: 'admin_form_type', col_label: 'Admin Form Type', col_type: 'text', is_custom: false, col_active: 1 },
                            { col_name: 'firstAdminUpdate', col_label: 'First Admin Update', col_type: 'dateTime', is_custom: false, col_active: 1 },
                            { col_name: 'preferred_call_time', col_label: 'Preferred Call Time', col_type: 'select', is_custom: false, col_active: 1 },
                        ];

                        // Date columns at the end
                        const dateColumns: Column[] = [
                            { col_name: 'created_date', col_label: 'Created Date', col_type: 'dateTime', is_custom: false, col_active: 1 },
                            { col_name: 'updated_date', col_label: 'Updated Date', col_type: 'dateTime', is_custom: false, col_active: 1 },
                        ];

                        // Merge with API columns to get options for select fields
                        // For admin tracking fields, use adminTrackingOptions
                        // For regular fields (status, grade), use user columns
                        // For city/area, use fetched options
                        const mergedAdminColumns = adminColumnsOrder.map(adminCol => {
                            // Handle city column
                            if (adminCol.col_name === 'schoolCity') {
                                return {
                                    ...adminCol,
                                    options: citiesOptions
                                };
                            }
                            // Handle area column
                            if (adminCol.col_name === 'schoolArea') {
                                return {
                                    ...adminCol,
                                    options: areasOptions
                                };
                            }
                            // Check if this is an admin tracking field with options
                            if (adminTrackingOptions[adminCol.col_name]) {
                                const trackingOpts = adminTrackingOptions[adminCol.col_name];
                                return {
                                    ...adminCol,
                                    uuid: trackingOpts.col_uuid,
                                    col_id: trackingOpts.col_id,
                                    options: trackingOpts.options || []
                                };
                            }
                            // For regular fields (status, grade), try to get from user columns
                            const apiCol = cols.find(c => c.col_name === adminCol.col_name);
                            return apiCol ? { ...apiCol, ...adminCol } : adminCol;
                        });

                        // Final column order
                        const sortedColumns = [...mergedAdminColumns, ...dateColumns];

                        // Visible columns for admin view
                        const adminVisibleColumns = new Set([
                            'phone', 'clientName', 'schoolCity', 'schoolArea', 'name', 'email',
                            'admin_source', 'admin_sub_source', 'status', 'admin_status', 'grade', 'last_remark', 'admin_last_remark',
                            'admin_is_qualified', 'admin_prospect', 'aiProspectStatus', 'admin_near_by_client', 'admin_form_type',
                            'firstAdminUpdate', 'preferred_call_time', 'created_date', 'updated_date'
                        ]);

                        console.log('📋 Admin columns order:', sortedColumns.map(c => c.col_name));
                        console.log('📋 Admin columns with options:', sortedColumns.filter(c => c.options?.length).map(c => `${c.col_name}(${c.options?.length})`));
                        console.log('📋 Admin visible columns:', Array.from(adminVisibleColumns));

                        setColumns(sortedColumns);
                        setVisibleColumns(adminVisibleColumns);
                        setColumnsLoaded(true);

                        // Fetch cities and areas in background — don't block lead loading
                        Promise.all([
                            optimizedApi.post('/cities', { limit: 500, sort_column: 'cityName', sort_order: 'ASC' }),
                            optimizedApi.post('/areas', { limit: 2000, sort_column: 'areaName', sort_order: 'ASC' })
                        ]).then(([citiesResponse, areasResponse]) => {
                            type BgColumnOption = { opt_id: number; opt_name: string; opt_value?: string };
                            let citiesOpts: BgColumnOption[] = [];
                            let areasOpts: BgColumnOption[] = [];

                            const citiesData = citiesResponse.data as { cities?: Array<{ cityId: number; cityName: string }> } | undefined;
                            if (citiesResponse.success && citiesData?.cities) {
                                citiesOpts = citiesData.cities.map((city) => ({
                                    opt_id: city.cityId,
                                    opt_name: city.cityName,
                                    opt_value: String(city.cityId)
                                }));
                                console.log('✅ Cities loaded (background):', citiesOpts.length);
                            }

                            const areasData = areasResponse.data as { areas?: Array<{ areaId: number; areaName: string }> } | undefined;
                            if (areasResponse.success && areasData?.areas) {
                                areasOpts = areasData.areas.map((area) => ({
                                    opt_id: area.areaId,
                                    opt_name: area.areaName,
                                    opt_value: String(area.areaId)
                                }));
                                console.log('✅ Areas loaded (background):', areasOpts.length);
                            }

                            setColumns(prev => prev.map(col => {
                                if (col.col_name === 'schoolCity') return { ...col, options: citiesOpts };
                                if (col.col_name === 'schoolArea') return { ...col, options: areasOpts };
                                return col;
                            }));
                        }).catch(error => {
                            console.error('❌ Error fetching cities/areas (background):', error);
                        });

                        return;
                    }

                    // NORMAL VIEW - Original logic
                    // Add built-in columns that are not in crm_leads_columns table
                    const additionalColumns: Column[] = [
                        { col_name: 'is_qualified', col_label: 'Qualified', col_type: 'boolean', is_custom: false, col_active: 1, user_column: false },
                        { col_name: 'created_date', col_label: 'Created Date', col_type: 'dateTime', is_custom: false, col_active: 1, user_column: true },
                        { col_name: 'updated_date', col_label: 'Updated Date', col_type: 'dateTime', is_custom: false, col_active: 1, user_column: true },
                        // One column for both: when the team first worked the lead and how long it waited
                        { col_name: 'response_time', col_label: 'First Updated Date', col_type: 'select', options: RESPONSE_TIME_OPTIONS, is_custom: false, col_active: 1, user_column: true },
                    ];

                    // Extended Lead Profile columns (crm_lead_details) — VIS Phase 5.2.
                    // Not EAV custom fields, so they're synthesized here rather than
                    // coming from /api/columns; gated by the client's lead-profile settings.
                    const toOpts = (values: string[]) => values.map((v) => ({ opt_id: v, opt_name: v }));
                    let extendedProfileColumns: Column[] = [];
                    let sectionsEnabled: LeadProfileSectionKey[] = [];
                    try {
                        const profileRes = await optimizedApi.get<LeadProfileSettings>('/lead-profile/settings');
                        const settings = profileRes.success ? profileRes.data : null;
                        sectionsEnabled = settings?.is_active ? (settings.sections_enabled || []) : [];
                        const hasSection = (key: LeadProfileSectionKey) => sectionsEnabled.includes(key);

                        // Every Vista Form field can be a column, named exactly as in the form (PROFILE_FIELD_LABELS).
                        // Select fields with options also appear under Advanced Filters → Additional Filters.
                        const profileCol = (col_name: string, col_type: string, options?: string[]): Column => ({
                            col_name, col_label: PROFILE_FIELD_LABELS[col_name] || col_name, col_type, is_custom: false, is_profile: true, col_active: 1,
                            ...(options ? { options: toOpts(options) } : {}),
                        } as Column);
                        if (hasSection('communication')) {
                            extendedProfileColumns.push(
                                profileCol('lead_source_category', 'select', Object.keys(LEAD_SOURCE_SUBCATEGORY_MAP)),
                                profileCol('lead_source_subcategory', 'select', [...new Set(Object.values(LEAD_SOURCE_SUBCATEGORY_MAP).flat())]),
                            );
                        }
                        if (hasSection('academic')) {
                            extendedProfileColumns.push(
                                profileCol('academic_year_joining', 'select', ACADEMIC_YEAR_OPTIONS),
                                profileCol('curriculum', 'select', CURRICULUM_OPTIONS),
                                profileCol('student_first_name', 'text'),
                                profileCol('student_last_name', 'text'),
                                profileCol('date_of_birth', 'date'),
                                profileCol('gender', 'select', GENDER_OPTIONS),
                            );
                        }
                        if (hasSection('guardian')) {
                            extendedProfileColumns.push(
                                profileCol('is_existing_parent', 'text'),
                                profileCol('father_name', 'text'),
                                profileCol('father_occupation', 'text'),
                                profileCol('father_company', 'text'),
                                profileCol('father_qualification', 'text'),
                                profileCol('mother_name', 'text'),
                                profileCol('mother_occupation', 'text'),
                                profileCol('mother_company', 'text'),
                                profileCol('mother_qualification', 'text'),
                            );
                        }
                        if (hasSection('residence')) {
                            extendedProfileColumns.push(
                                profileCol('current_city', 'text'),
                                profileCol('current_country', 'text'),
                                profileCol('is_local', 'text'),
                                profileCol('residential_address', 'text'),
                            );
                        }
                        if (hasSection('brand_awareness')) {
                            extendedProfileColumns.push(profileCol('brand_awareness', 'select', BRAND_AWARENESS_OPTIONS));
                        }
                        if (hasSection('communication')) {
                            extendedProfileColumns.push(
                                profileCol('is_contactable', 'select', IS_CONTACTABLE_OPTIONS),
                                profileCol('last_contacted_date', 'date'),
                                profileCol('next_followup_date', 'date'),
                            );
                        }
                        if (settings?.is_active) {
                            extendedProfileColumns.push(profileCol('summary_notes', 'text'));
                        }
                        if (hasSection('qualification_status')) {
                            extendedProfileColumns.push(
                                profileCol('counsellor_qualification', 'select', COUNSELLOR_QUALIFICATION_OPTIONS),
                                profileCol('disqualified_reason', 'select', DISQUALIFIED_REASONS),
                                profileCol('disqualified_reason_notes', 'text'),
                                profileCol('overall_status', 'select', OVERALL_STATUS_OPTIONS),
                                profileCol('counsellor_priority', 'select', COUNSELLOR_PRIORITY_OPTIONS),
                                profileCol('closed_dead_reason', 'select', CLOSED_DEAD_REASONS),
                                profileCol('competitor_school_joined', 'text'),
                                profileCol('closed_dead_reason_notes', 'text'),
                                profileCol('considerations', 'select', CONSIDERATION_OPTIONS),
                                profileCol('stage_of_admission', 'select', STAGE_OF_ADMISSION_STEPS),
                            );
                        }
                        if (settings?.is_active) {
                            extendedProfileColumns.push(profileCol('admission_date', 'date'));
                        }
                    } catch (error) {
                        console.error('❌ Error fetching lead profile settings (Extended Profile columns skipped):', error);
                    }

                    // When Extended Profile is active for this client, its per-section fields
                    // replace the equivalent native column rather than sitting alongside it:
                    // communication -> Lead Source Category/Sub-category replace native Source;
                    // qualification_status -> Overall Status/Stage of Admission/Counsellor Priority
                    // replace native Status. Removing them here (not just from default visibility)
                    // also drops them from the column picker and native source/status filtering,
                    // since both are driven off this same column list.
                    const nativeColsToReplace = new Set<string>();
                    if (sectionsEnabled.includes('communication')) nativeColsToReplace.add('source');
                    if (sectionsEnabled.includes('qualification_status')) nativeColsToReplace.add('status');
                    const colsAfterReplacement = nativeColsToReplace.size > 0
                        ? cols.filter(c => !nativeColsToReplace.has(c.col_name))
                        : cols;

                    // Native columns that also appear in the Vista Form take the form's name.
                    if (extendedProfileColumns.length > 0) {
                        const vistaNames: Record<string, string> = {
                            created_date: 'Lead Created Date', owner: 'Counsellor Assigned',
                            name: 'Contact Name', phone: 'Contact Phone', email: 'Contact Email',
                        };
                        colsAfterReplacement.forEach((c) => { if (vistaNames[c.col_name]) c.col_label = vistaNames[c.col_name]; });
                        additionalColumns.forEach((c) => { if (vistaNames[c.col_name]) c.col_label = vistaNames[c.col_name]; });
                    }

                    const existingColNames = new Set(colsAfterReplacement.map(c => c.col_name));
                    const columnsToAdd = additionalColumns.filter(c => !existingColNames.has(c.col_name));
                    const allColumns = [...colsAfterReplacement, ...columnsToAdd, ...extendedProfileColumns];

                    // Vista Form clients: every dropdown filter also offers "Blank / N/A" so leads with an
                    // empty field can be found. Counsellor already has Unassigned; First Updated Date has its own buckets.
                    if (extendedProfileColumns.length > 0) {
                        allColumns.forEach((c) => {
                            if (c.col_type !== 'select' || c.col_name === 'owner' || c.col_name === 'response_time') return;
                            if (c.options?.some((o) => o.opt_id === BLANK_FILTER_VALUE)) return;
                            c.options = [{ opt_id: BLANK_FILTER_VALUE, opt_name: BLANK_FILTER_LABEL }, ...(c.options || [])];
                        });
                    }

                    // Normal view sorting:
                    // 1. Phone first (fixed)
                    // 2. Regular columns by col_order ASC, then by default order for same col_order
                    // 3. Owner at the end
                    // 4. Date columns (created_date, updated_date) at the very end
                    // 5. Tracking fields after date columns
                    const endColumns = ['owner', 'created_date', 'response_time', 'updated_date'];

                    // Default order for primary columns when col_order is same
                    const defaultColumnOrder: Record<string, number> = {
                        'phone': 1,
                        'name': 2,
                        'email': 3,
                        'source': 4,
                        'status': 5,
                        'grade': 6,
                        'alternative_phone': 7,
                        // Extended Profile replacements for source/status (see nativeColsToReplace above).
                        // These have no col_order from crm_leads_columns, so they sort into the usual
                        // Extended-Profile cluster after native columns; this only orders them relative
                        // to each other and ahead of other Extended Profile columns (which fall back to 50).
                        'lead_source_category': 4.1,
                        'lead_source_subcategory': 4.2,
                        'overall_status': 5.1,
                        'stage_of_admission': 5.2,
                        'counsellor_priority': 5.3,
                    };

                    const sortedColumns = allColumns.sort((a, b) => {
                        // Phone always first
                        if (a.col_name === 'phone') return -1;
                        if (b.col_name === 'phone') return 1;

                        const aIsTracking = a.is_tracking_field === true;
                        const bIsTracking = b.is_tracking_field === true;
                        const aIsEnd = endColumns.includes(a.col_name);
                        const bIsEnd = endColumns.includes(b.col_name);

                        // Tracking fields go to the very end
                        if (!aIsTracking && bIsTracking) return -1;
                        if (aIsTracking && !bIsTracking) return 1;

                        // End columns (owner, dates) go after regular columns but before tracking
                        if (!aIsEnd && bIsEnd && !bIsTracking) return -1;
                        if (aIsEnd && !bIsEnd && !aIsTracking) return 1;

                        // Sort tracking fields by category
                        if (aIsTracking && bIsTracking) {
                            if (a.category !== b.category) {
                                const categoryOrder: Record<string, number> = {
                                    'UTM Tracking': 1,
                                    'Facebook Tracking': 2,
                                    'LinkedIn Tracking': 3,
                                };
                                return (categoryOrder[a.category || ''] || 99) - (categoryOrder[b.category || ''] || 99);
                            }
                            return (a.col_label || '').localeCompare(b.col_label || '');
                        }

                        // Sort end columns: owner, then created_date, then updated_date
                        if (aIsEnd && bIsEnd) {
                            const endOrder: Record<string, number> = {
                                'owner': 1,
                                'created_date': 2,
                                'response_time': 3,
                                'updated_date': 4
                            };
                            return (endOrder[a.col_name] || 99) - (endOrder[b.col_name] || 99);
                        }

                        // Sort regular columns by col_order ASC
                        if (!aIsTracking && !bIsTracking && !aIsEnd && !bIsEnd) {
                            const aOrder = a.col_order ?? 9999;
                            const bOrder = b.col_order ?? 9999;

                            // If col_order is different, use it
                            if (aOrder !== bOrder) {
                                return aOrder - bOrder;
                            }

                            // If col_order is same, use default order for known columns
                            const aDefault = defaultColumnOrder[a.col_name] ?? 50;
                            const bDefault = defaultColumnOrder[b.col_name] ?? 50;
                            if (aDefault !== bDefault) {
                                return aDefault - bDefault;
                            }

                            // Fallback to alphabetical by label
                            return (a.col_label || '').localeCompare(b.col_label || '');
                        }

                        return 0;
                    });

                    // Load column preferences
                    const storedPreferences = loadColumnPreferencesFromStorage();
                    let initialVisible: Set<string>;

                    if (storedPreferences && storedPreferences.size > 0) {
                        initialVisible = storedPreferences;
                    } else {
                        const userSelectedColumns = sortedColumns
                            .filter(col => col.user_column === true)
                            .map(col => col.col_name);

                        if (userSelectedColumns.length > 0) {
                            initialVisible = new Set(userSelectedColumns);
                            saveColumnPreferencesToStorage(initialVisible);
                        } else {
                            initialVisible = new Set(['phone', 'name', 'email', 'status', 'source', 'owner', 'created_date', 'updated_date']);
                        }
                    }

                    initialVisible.add('phone');
                    initialVisible.add('owner');
                    initialVisible.add('created_date');

                    // First Updated Date is new: show it once to users whose saved column choice
                    // predates it; after that, hiding it sticks like any other column.
                    try {
                        const FIRST_UPDATED_SHOWN_KEY = 'crm_first_updated_columns_shown';
                        if (!localStorage.getItem(FIRST_UPDATED_SHOWN_KEY)) {
                            initialVisible.add('response_time');
                            localStorage.setItem(FIRST_UPDATED_SHOWN_KEY, '1');
                            saveColumnPreferencesToStorage(initialVisible);
                        }
                    } catch {
                        // Storage unavailable: fall back to the saved/default choice
                    }

                    // Force-show the Extended Profile replacement columns even for users with
                    // stored column preferences that predate this replacement (source/status are
                    // gone from `columns` entirely when replaced, so they won't render regardless).
                    if (nativeColsToReplace.has('source')) {
                        initialVisible.add('lead_source_category');
                        initialVisible.add('lead_source_subcategory');
                    }
                    if (extendedProfileColumns.some(c => c.col_name === 'summary_notes')) {
                        initialVisible.add('summary_notes');
                    }
                    if (nativeColsToReplace.has('status')) {
                        initialVisible.add('overall_status');
                        initialVisible.add('stage_of_admission');
                        initialVisible.add('counsellor_priority');
                    }

                    setColumns(sortedColumns);
                    setVisibleColumns(initialVisible);
                    setColumnsLoaded(true);
                } else {
                    console.error('❌ Invalid columns response:', response);
                }
            } catch (error) {
                console.error('❌ Error fetching columns:', error);
            }
        };
        
        fetchColumns();
    }, []); // Empty dependency array - run only once on mount

    // Step 2: Fetch leads when columns are loaded or when filters/pagination changes
    const fetchLeads = useCallback(async () => {
        // Only fetch if columns are loaded
        if (!columnsLoaded) {
            console.log('⏳ Waiting for columns to load before fetching leads');
            return;
        }
        
        console.log('🔄 Step 2: Fetching leads...');
        
        // Map updated_date to last_updated_date for backend compatibility
        const backendSortColumn = sortColumn === 'updated_date' ? 'last_updated_date' : sortColumn;

        const payload: LeadsPayload = {
            page: pagination.currentPage,
            limit: pagination.itemsPerPage,
            sort_by: backendSortColumn,
            sort_order: sortDirection,
            primary_filters: {},
            custom_filters: {},
            profile_filters: {}
        };

        // Add search term to payload
        if (searchTerm && searchTerm.trim()) {
            payload.primary_filters!['search'] = searchTerm.trim();
        }

        // Create a map of column names to column metadata for quick lookup
        const columnMap = new Map<string, Column>();
        columns.forEach(col => {
            columnMap.set(col.col_name, col);
        });

        // Add filters to payload
        Object.entries(activeFilters).forEach(([key, filterItem]) => {
            // Type guard to ensure filterItem has the expected structure
            const filter = filterItem as CustomFieldFilter;

            if (!filter) return;

            // Map updated_date to last_updated_date for backend compatibility
            const backendKey = key === 'updated_date' ? 'last_updated_date' : key;

            // Look up column metadata to determine if it's a custom field
            const columnMetadata = columnMap.get(key);
            const isCustomField = columnMetadata?.is_custom === true;
            const isProfileField = columnMetadata?.is_profile === true;
            const columnUuid = columnMetadata?.uuid;

            // Handle date/dateTime filters (they have from/to instead of value)
            if (filter.type === 'date' || filter.type === 'dateTime') {
                // Only send date filters with non-empty values
                const dateFilter: any = {};
                if (filter.from && filter.from !== '') dateFilter.from = filter.from;
                if (filter.to && filter.to !== '') dateFilter.to = filter.to;

                // Only add to payload if at least one date is set
                if (Object.keys(dateFilter).length > 0) {
                    if (isCustomField && columnUuid) {
                        // Custom field date filter - add to custom_filters with column UUID
                        payload.custom_filters![columnUuid] = dateFilter;
                    } else if (isProfileField) {
                        // Extended Lead Profile date filter - add to profile_filters by col_name
                        payload.profile_filters![backendKey] = dateFilter;
                    } else {
                        // Primary field date filter
                        payload.primary_filters![backendKey] = dateFilter;
                    }
                }
                return;
            }

            // For non-date filters, check if value exists
            if (filter.value === undefined || filter.value === null || filter.value === '') {
                return;
            }

            // Handle owner filter - send UUIDs to the backend
            if (key === 'owner') {
                if (Array.isArray(filter.value) && filter.value.length > 0) {
                    // Send owner UUIDs to the backend
                    payload.primary_filters!['owner_uuids'] = filter.value;
                }
                return;
            }

            // Handle is_qualified and admin_is_qualified filters - convert string to number
            if (key === 'is_qualified' || key === 'admin_is_qualified') {
                payload.primary_filters![key] = parseInt(String(filter.value));
                return;
            }

            // Handle select filters
            if (filter.type === 'select' && Array.isArray(filter.value) && filter.value.length > 0) {
                if (isCustomField && columnUuid) {
                    // Custom field select - add to custom_filters with column UUID
                    payload.custom_filters![columnUuid] = filter.value;
                } else if (isProfileField) {
                    // Extended Lead Profile select - add to profile_filters by col_name
                    payload.profile_filters![backendKey] = filter.value;
                } else {
                    // Primary field select - add to primary_filters
                    payload.primary_filters![backendKey] = filter.value;
                }
            }
            // Handle text filters
            else if (!Array.isArray(filter.value) && filter.value !== '') {
                if (isCustomField && columnUuid) {
                    // Custom field text - add to custom_filters with column UUID
                    payload.custom_filters![columnUuid] = filter.value;
                } else if (isProfileField) {
                    // Extended Lead Profile text - add to profile_filters by col_name
                    payload.profile_filters![backendKey] = filter.value;
                } else {
                    // Primary field text - add to primary_filters
                    payload.primary_filters![backendKey] = filter.value;
                }
            }
        });

        try {
            setIsLoading(true);
            console.log('🚀 Making leads API call with payload:', payload, 'to endpoint:', apiEndpoint);
            const result = await optimizedApi.post(apiEndpoint, payload);
            console.log('📥 Leads API response:', result);
            
            if (result.success) {
                const leadsData = Array.isArray(result.data) ? result.data : [];
                console.log('✅ Setting leads data:', leadsData.length, 'leads');

                // No need for client-side owner filtering anymore since it's handled by backend
                setLeads(leadsData);
                setPagination(prev => ({
                    ...prev,
                    totalPages: result.totalPages || 1,
                    totalItems: result.total || 0
                }));
            } else {
                console.log('❌ Leads API unsuccessful:', result.message);
            }
        } catch (error) {
            console.error('❌ Error fetching leads:', error);
        } finally {
            setIsLoading(false);
        }
    }, [columnsLoaded, pagination.currentPage, pagination.itemsPerPage, sortColumn, sortDirection, activeFilters, searchTerm]);

    // Trigger leads fetch when columns are loaded or dependencies change
    useEffect(() => {
        if (columnsLoaded) {
            console.log('📍 Columns loaded, now fetching leads');
            fetchLeads();
        }
    }, [columnsLoaded, fetchLeads]);

    // Expose refresh method to parent component via ref
    useImperativeHandle(ref, () => ({
        refreshLeads: async () => {
            console.log('🔄 Manual refresh triggered from parent');
            await fetchLeads();
        }
    }), [fetchLeads]);

    // Handle column sorting
    const handleSort = (columnName: string) => {
        if (sortColumn === columnName) {
            setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortColumn(columnName);
            setSortDirection('ASC');
        }
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    // Handle filter changes (immediate input changes)
    const handleFilterChange = (columnName: string, value: any, type: 'text' | 'select' | 'date' | 'dateTime') => {

        if (type === 'date' || type === 'dateTime') {
            // Handle date range filters
            setTempFilters(prev => {
                const prevFilter = prev[columnName] as CustomFieldFilter | undefined;
                return {
                    ...prev,
                    [columnName]: {
                        ...(prevFilter || {}),
                        ...value,
                        type
                    }
                };
            });
        } else if (type === 'select') {
            // Handle multi-select filters
            const newFilterValue = { value: Array.isArray(value) ? value : [value].filter(Boolean), type };
            setTempFilters(prev => ({
                ...prev,
                [columnName]: newFilterValue
            }));
        } else {
            setTempFilters(prev => ({
                ...prev,
                [columnName]: { value, type }
            }));
        }
    };

    // Apply filters (when user confirms)
    const applyFilters = () => {
        setActiveFilters({ ...tempFilters });
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    // Toggle filter popover
    const handleFilterIconClick = (columnName: string, event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation(); // Prevent sort from triggering

        if (filterPopoverColumn === columnName) {
            // Close if already open
            setFilterPopoverColumn(null);
            setFilterPopoverPosition(null);
        } else {
            // Open popover for this column
            const rect = event.currentTarget.getBoundingClientRect();
            const popoverWidth = 320; // max-width of popover
            const viewportWidth = window.innerWidth;

            // Calculate left position, ensuring popover stays within viewport
            let left = rect.left + window.scrollX;
            if (left + popoverWidth > viewportWidth) {
                // Align to right edge of button instead
                left = rect.right + window.scrollX - popoverWidth;
            }

            setFilterPopoverColumn(columnName);
            setFilterPopoverPosition({
                top: rect.bottom + window.scrollY + 4,
                left: Math.max(10, left) // Ensure at least 10px from left edge
            });
        }
    };

    // Close filter popover
    const closeFilterPopover = () => {
        setFilterPopoverColumn(null);
        setFilterPopoverPosition(null);
    };

    // Check if column has active filter
    const hasActiveFilter = (columnName: string): boolean => {
        const filter = activeFilters[columnName] as CustomFieldFilter | undefined;
        if (!filter) return false;

        if (filter.type === 'date' || filter.type === 'dateTime') {
            return !!(filter.from || filter.to);
        }
        if (Array.isArray(filter.value)) {
            return filter.value.length > 0;
        }
        return !!filter.value;
    };

    // Check if there are filters to apply
    const hasFiltersToApply = useMemo(() => {
        return Object.keys(tempFilters).some(key => {
            const tempValue = tempFilters[key] as CustomFieldFilter | undefined;
            const activeValue = activeFilters[key] as CustomFieldFilter | undefined;

            // Handle array values (multi-select)
            if (Array.isArray(tempValue?.value) && Array.isArray(activeValue?.value)) {
                return JSON.stringify(tempValue.value.sort()) !== JSON.stringify(activeValue.value.sort());
            }

            // Handle date range values
            if (tempValue?.type === 'date' || tempValue?.type === 'dateTime') {
                return tempValue?.from !== activeValue?.from || tempValue?.to !== activeValue?.to;
            }

            // Handle other values
            return tempValue?.value !== activeValue?.value;
        }) || Object.keys(tempFilters).length !== Object.keys(activeFilters).length;
    }, [tempFilters, activeFilters]);

    // Clear all filters
    const clearFilters = () => {
        setActiveFilters({});
        setTempFilters({});
        setSearchTerm('');
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    // Handle search
    const handleSearch = useCallback((term: string) => {
        setSearchTerm(term);
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    }, []);

    // Convert pipeline filters to leads table format
    const convertPipelineFiltersToLeadsFormat = useCallback((pipelineFilters: any) => {
        const leadsFilters: FilterState = {};

        console.log('Converting pipeline filters:', pipelineFilters);

        // Handle flat structure from PipelineFiltersEnhanced
        const textFields = ['phone', 'name', 'email'];
        const dateFields = ['created_date', 'updated_date', 'next_followup_date'];

        Object.entries(pipelineFilters).forEach(([key, value]: [string, any]) => {
            // Skip if value is empty
            if (!value) return;

            // Text filters
            if (textFields.includes(key) && typeof value === 'string') {
                leadsFilters[key] = {
                    value: value,
                    type: 'text'
                };
            }
            // Date filters
            else if (dateFields.includes(key) && typeof value === 'object' && (value.startDate || value.endDate)) {
                leadsFilters[key] = {
                    value: value,
                    type: 'date',
                    from: value.startDate,
                    to: value.endDate
                };
            }
            // Select filters (arrays)
            else if (Array.isArray(value) && value.length > 0) {
                leadsFilters[key] = {
                    value: value,
                    type: 'select'
                };
            }
        });

        console.log('Converted to leads filters:', leadsFilters);
        return leadsFilters;
    }, []);

    // Convert leads filters to pipeline format (flat structure for PipelineFiltersEnhanced)
    const convertLeadsFiltersToPipelineFormat = useCallback((leadsFilters: FilterState) => {
        const pipelineFilters: Record<string, any> = {};

        Object.entries(leadsFilters).forEach(([key, filterItem]) => {
            const filter = filterItem as CustomFieldFilter;
            if (!filter) return;

            if (filter.type === 'text') {
                // Text filters: return plain string value
                pipelineFilters[key] = filter.value;
            } else if (filter.type === 'date' || filter.type === 'dateTime') {
                // Date filters: convert from {from, to} to {startDate, endDate}
                pipelineFilters[key] = {
                    startDate: filter.from || '',
                    endDate: filter.to || ''
                };
            } else if (filter.type === 'select') {
                // Select filters: return array value directly
                pipelineFilters[key] = filter.value;
            }
        });

        return pipelineFilters;
    }, []);

    // Handle advanced filters apply
    const handleAdvancedFiltersApply = useCallback((pipelineFilters: any) => {
        const convertedFilters = convertPipelineFiltersToLeadsFormat(pipelineFilters);
        setActiveFilters(convertedFilters);
        setTempFilters(convertedFilters);
        setPagination(prev => ({ ...prev, currentPage: 1 }));
        setIsAdvancedFiltersOpen(false);
    }, [convertPipelineFiltersToLeadsFormat]);

    // Handle row selection
    const handleRowSelect = (leadUuid: string, checked: boolean) => {
        const newSelection = new Set(selectedRows);
        if (checked) {
            newSelection.add(leadUuid);
        } else {
            newSelection.delete(leadUuid);
        }
        setSelectedRows(newSelection);
        setSelectAll(newSelection.size === leads.length && leads.length > 0);
        
        onRowSelect?.(Array.from(newSelection));
    };

    // Handle select all
    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            const allIds = new Set(leads.map(lead => lead.uuid));
            setSelectedRows(allIds);
            onRowSelect?.(Array.from(allIds));
        } else {
            setSelectedRows(new Set());
            onRowSelect?.([]);
        }
        setSelectAll(checked);
    };

    // Handle pagination
    const handlePageChange = (page: number) => {
        setPagination(prev => ({ ...prev, currentPage: page }));
    };

    const handleItemsPerPageChange = (itemsPerPage: number) => {
        setPagination(prev => ({
            ...prev,
            itemsPerPage,
            currentPage: 1
        }));
    };

    // Outbound calling handlers
    const handleInitiateCall = (lead: Lead, event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent opening lead details

        if (isCallInProgress) {
            showToast('Another call is already in progress', 'error');
            return;
        }

        const phoneNumber = getCellValue(lead, 'phone');
        if (!phoneNumber) {
            showToast('No phone number available for this lead', 'error');
            return;
        }

        setSelectedCallLead({
            uuid: lead.uuid,
            name: getCellValue(lead, 'name') || 'Unknown',
            phone: phoneNumber,
            country_code: lead.country_code || null
        });
        setIsCallModalOpen(true);
        setCallInProgress(true);
    };

    const handleCloseCallModal = () => {
        setIsCallModalOpen(false);
        setSelectedCallLead(null);
        setCallInProgress(false);
    };

    const handleRunoClickToCall = async (lead: Lead, event: React.MouseEvent) => {
        event.stopPropagation();

        const phoneNumber = getCellValue(lead, 'phone');
        if (!phoneNumber) {
            showToast('No phone number available for this lead', 'error');
            return;
        }

        setRunoCallingLeadId(lead.uuid);
        try {
            const response = await apiRequest('/api/runo/click-to-call', {
                method: 'POST',
                body: JSON.stringify({ lead_uuid: lead.uuid })
            });
            if (response.success) {
                showToast('✅ Call placed successfully! Your phone will ring shortly.', 'success', 5000);
            } else {
                showToast(response.message || 'Failed to trigger Runo call', 'error', 5000);
            }
        } catch {
            showToast('Failed to place call. Please try again.', 'error', 5000);
        } finally {
            setRunoCallingLeadId(null);
        }
    };

    // Get cell value with proper formatting
    const getCellValue = useCallback((lead: Lead, columnName: string): string => {
        // Handle special computed columns
        if (columnName === 'owner') {
            // Get owner from assignments array
            const assignments = (lead as any).assignments;
            if (assignments && Array.isArray(assignments) && assignments.length > 0) {
                // Find the active assignment
                const activeAssignment = assignments.find((assignment: any) => assignment.is_active === true);
                if (activeAssignment && activeAssignment.user && activeAssignment.user.userName) {
                    return activeAssignment.user.userName;
                }
            }
            return '--';
        }
        if (columnName === 'last_remark') {
            // Get the latest remark from the remarks array, filtering for normal, user, or audio remarks
            const remarks = (lead as any).remarks;
            if (remarks && Array.isArray(remarks) && remarks.length > 0) {
                // Filter for normal, user, or audio remarks and find the most recent one
                const filteredRemarks = remarks.filter((remark: any) => {
                    const remarkType = remark.remark_type;

                    // For audio remarks, only exclude if source is explicitly "None"
                    if (remarkType === 'audio') {
                        return remark.source !== 'None';
                    }

                    return remarkType === 'normal' || remarkType === 'user';
                });

                if (filteredRemarks.length > 0) {
                    // Sort by date to get the most recent (try multiple date fields)
                    const sortedRemarks = filteredRemarks.sort((a: any, b: any) => {
                        // Try different date fields that might exist
                        const dateA = new Date(a.date_remark || a.formattedDate || a.createdAt || a.created_at || 0);
                        const dateB = new Date(b.date_remark || b.formattedDate || b.createdAt || b.created_at || 0);
                        return dateB.getTime() - dateA.getTime(); // Most recent first
                    });

                    const latestRemark = sortedRemarks[0];
                    if (latestRemark.remark_type === 'audio') {
                        return '🎵 Audio Recording';
                    }
                    const remarkText = latestRemark.remark || 'No remark text';
                    // Truncate long remarks for table display
                    return remarkText.length > 50 ? remarkText.substring(0, 50) + '...' : remarkText;
                }
            }
            return 'No remarks';
        }
        if (columnName === 'admin_last_remark') {
            // Get the latest admin remark from the remarks array
            const remarks = (lead as any).remarks;
            if (remarks && Array.isArray(remarks) && remarks.length > 0) {
                // Filter for admin_user remarks only (from admin tracking form)
                const adminRemarks = remarks.filter((remark: any) => remark.remark_type === 'admin_user');

                if (adminRemarks.length > 0) {
                    // Sort by date to get the most recent
                    const sortedRemarks = adminRemarks.sort((a: any, b: any) => {
                        const dateA = new Date(a.date_remark || a.formattedDate || a.createdAt || a.created_at || 0);
                        const dateB = new Date(b.date_remark || b.formattedDate || b.createdAt || b.created_at || 0);
                        return dateB.getTime() - dateA.getTime(); // Most recent first
                    });

                    const latestRemark = sortedRemarks[0];
                    const remarkText = latestRemark.remark || 'No remark text';
                    // Truncate long remarks for table display
                    return remarkText.length > 50 ? remarkText.substring(0, 50) + '...' : remarkText;
                }
            }
            return 'No admin remarks';
        }

        // Handle special display properties
        if (columnName === 'status') {
            return lead.statusDisplay || lead.statusOption?.opt_name || String(lead.status || 'N/A');
        }
        if (columnName === 'source') {
            return lead.sourceDisplay || lead.sourceOption?.opt_name || String(lead.source || 'N/A');
        }
        if (columnName === 'grade') {
            return lead.gradeDisplay || lead.gradeOption?.opt_name || (lead.grade ? String(lead.grade) : 'N/A');
        }
        if (columnName === 'admin_sub_source') {
            // For admin view, use admin tracking data
            const adminTracking = (lead as any).adminTrackingData;
            if (adminTracking?.admin_sub_source?.display) {
                return adminTracking.admin_sub_source.display;
            }
            return 'N/A';
        }
        if (columnName === 'admin_status') {
            // Admin status from admin tracking data
            const adminTracking = (lead as any).adminTrackingData;
            if (adminTracking?.admin_status?.display) {
                return adminTracking.admin_status.display;
            }
            return 'N/A';
        }

        // Handle admin-specific columns
        if (columnName === 'clientName') {
            return (lead as any).clientName || 'N/A';
        }
        if (columnName === 'adminStatusDisplay') {
            return (lead as any).adminStatusDisplay || 'N/A';
        }
        if (columnName === 'adminCurriculumDisplay') {
            return (lead as any).adminCurriculumDisplay || 'N/A';
        }
        if (columnName === 'adminSubStatusDisplay') {
            return (lead as any).adminSubStatusDisplay || 'N/A';
        }
        if (columnName === 'adminCategoryDisplay') {
            return (lead as any).adminCategoryDisplay || 'N/A';
        }
        if (columnName === 'adminSubSourceDisplay') {
            return (lead as any).adminSubSourceDisplay || 'N/A';
        }
        if (columnName === 'adminBudget') {
            return (lead as any).adminBudget || 'N/A';
        }
        if (columnName === 'adminIsQualified') {
            return (lead as any).adminIsQualified ? 'Yes' : 'No';
        }

        // Handle new admin tracking columns
        if (columnName === 'admin_source') {
            const adminTracking = (lead as any).adminTrackingData;
            if (adminTracking?.admin_source?.display) {
                return adminTracking.admin_source.display;
            }
            return 'N/A';
        }
        if (columnName === 'last_remark') {
            return (lead as any).last_remark || 'N/A';
        }
        if (columnName === 'admin_last_remark') {
            return (lead as any).admin_last_remark || 'N/A';
        }
        if (columnName === 'admin_is_qualified') {
            const adminTracking = (lead as any).adminTrackingData;
            if (adminTracking?.admin_is_qualified === 1 || adminTracking?.admin_is_qualified === true) {
                return 'Yes';
            }
            return 'No';
        }
        if (columnName === 'admin_prospect') {
            const adminTracking = (lead as any).adminTrackingData;
            return adminTracking?.admin_prospect || 'N/A';
        }
        if (columnName === 'aiProspectStatus') {
            const status = (lead as any).aiProspectStatus;
            if (!status) return 'N/A';
            // Capitalize first letter
            return status.charAt(0).toUpperCase() + status.slice(1);
        }
        if (columnName === 'admin_near_by_client') {
            const adminTracking = (lead as any).adminTrackingData;
            return adminTracking?.admin_near_by_client || 'N/A';
        }
        if (columnName === 'admin_form_type') {
            const adminTracking = (lead as any).adminTrackingData;
            return adminTracking?.admin_form_type || 'N/A';
        }

        if (columnName === 'is_qualified') {
            // Display qualified status
            if (lead.is_qualified === 1 || lead.is_qualified === true) {
                return 'Yes';
            } else {
                return 'No';
            }
        }
        
        // Handle updated_date specifically
        if (columnName === 'updated_date') {
            const value = (isAdminView && (lead as any).adminTrackingData?.admin_last_updated_date)
                || lead.last_updated_date
                || lead.updated_date;
            if (value && typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
                try {
                    return format(parseISO(value), 'MMM dd, yyyy HH:mm');
                } catch {
                    return value;
                }
            }
            return value ? String(value) : 'N/A';
        }

        // First Updated Date column: the date plus how long the lead waited, e.g. "Oct 29, 2026 08:56 (14h 30m)"
        if (columnName === 'response_time') {
            const value = (lead as any).first_updated_date;
            if (value && typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
                const waited = formatResponseTime(lead.created_date as string | undefined, value);
                try {
                    return `${format(parseISO(value), 'MMM dd, yyyy HH:mm')} (${waited})`;
                } catch {
                    return `${value} (${waited})`;
                }
            }
            return 'Not updated yet';
        }

        // Handle firstAdminUpdate (first admin user remark date)
        if (columnName === 'firstAdminUpdate') {
            const value = (lead as any).firstAdminUpdate;
            if (value && typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
                try {
                    return format(parseISO(value), 'MMM dd, yyyy HH:mm');
                } catch {
                    return value;
                }
            }
            return value ? String(value) : 'N/A';
        }

        // Handle direct properties
        if (lead[columnName] !== undefined && lead[columnName] !== null) {
            const value = lead[columnName];
            
            if (typeof value === 'string') {
                // Format dates
                if (columnName.includes('date') && value.match(/^\d{4}-\d{2}-\d{2}/)) {
                    try {
                        return format(parseISO(value), 'MMM dd, yyyy HH:mm');
                    } catch {
                        return value;
                    }
                }
                return value;
            } else if (typeof value === 'boolean') {
                return value ? 'Yes' : 'No';
            } else {
                return String(value);
            }
        }

        // Handle custom fields - Check formattedCustomInfo first (preferred format)
        if (lead.formattedCustomInfo && lead.formattedCustomInfo[columnName]) {
            return lead.formattedCustomInfo[columnName].display || String(lead.formattedCustomInfo[columnName].value);
        }

        // Fallback to customFields array (new API format)
        if (lead.customFields && Array.isArray(lead.customFields)) {
            const customField = lead.customFields.find(
                field => field.column?.col_name === columnName
            );

            if (customField) {
                // Check optionDetails first (new format), then optionDetailsbyUuid (old format)
                if (customField.optionDetails?.opt_name) {
                    return customField.optionDetails.opt_name;
                } else if (customField.optionDetailsbyUuid?.opt_name) {
                    return customField.optionDetailsbyUuid.opt_name;
                } else if (customField.value_text !== undefined && customField.value_text !== null) {
                    return String(customField.value_text);
                } else if (customField.value_int !== undefined && customField.value_int !== null) {
                    return String(customField.value_int);
                } else if (customField.value_date !== undefined && customField.value_date !== null) {
                    return String(customField.value_date);
                } else if (customField.custom_field_value !== undefined) {
                    return String(customField.custom_field_value);
                }
            }
        }

        // Legacy support: Check customInfo array (old API format)
        if (lead.customInfo && Array.isArray(lead.customInfo)) {
            const customField = lead.customInfo.find(
                field => field.column?.col_name === columnName
            );

            if (customField) {
                if (customField.optionDetailsbyUuid?.opt_name) {
                    return customField.optionDetailsbyUuid.opt_name;
                } else if (customField.custom_field_value !== undefined) {
                    return String(customField.custom_field_value);
                }
            }
        }

        // Extended Lead Profile fields (crm_lead_details, eager-loaded as lead.profileDetails)
        const profileDetails = (lead as any).profileDetails;
        if (profileDetails && columnName === 'considerations') {
            const picked = [profileDetails.consideration_1, profileDetails.consideration_2].filter(Boolean);
            return picked.length > 0 ? picked.join(', ') : 'N/A';
        }
        if (profileDetails && profileDetails[columnName] !== undefined && profileDetails[columnName] !== null) {
            const value = profileDetails[columnName];
            if (Array.isArray(value)) {
                return value.length > 0 ? value.join(', ') : 'N/A';
            }
            if (typeof value === 'string' && columnName.includes('date') && value.match(/^\d{4}-\d{2}-\d{2}/)) {
                try {
                    return format(parseISO(value), 'MMM dd, yyyy');
                } catch {
                    return value;
                }
            }
            if (typeof value === 'boolean') {
                return value ? 'Yes' : 'No';
            }
            return String(value);
        }

        return 'N/A';
    }, []);

    // Render filter input based on column type
    const renderFilterInput = (column: Column) => {
        // Skip owner column as it has its own toolbar filter
        // Skip last_remark, admin_last_remark and summary_notes columns as they shouldn't have a filter
        // Skip Client Status in admin view (varies per client, not useful for filtering)
        if (column.col_name === 'owner' || column.col_name === 'last_remark' || column.col_name === 'admin_last_remark' || column.col_name === 'summary_notes' || (isAdminView && column.col_name === 'status')) {
            return null;
        }

        const filterValue = tempFilters[column.col_name] as CustomFieldFilter | undefined;
        const currentValue = filterValue?.value || [];

        // Special handling for is_qualified and admin_is_qualified columns
        if (column.col_name === 'is_qualified' || column.col_name === 'admin_is_qualified') {
            const qualifiedFilterValue = tempFilters[column.col_name] as CustomFieldFilter | undefined;
            return (
                <select
                    value={qualifiedFilterValue?.value || ''}
                    onChange={(e) => handleFilterChange(column.col_name, e.target.value, 'select')}
                    className="w-full px-2 py-1 text-sm font-normal border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-brandColor focus:border-transparent"
                >
                    <option value="">All</option>
                    <option value="1">Yes</option>
                    <option value="0">No</option>
                </select>
            );
        }

        switch (column.col_type) {
            case 'select':
                return (
                    <div className="relative">
                        <FSelect
                            options={column.options || []}
                            value={Array.isArray(currentValue) ? currentValue : []}
                            onChange={(selectedValues) => handleFilterChange(column.col_name, selectedValues, 'select')}
                            placeholder="Filter..."
                            searchable={true}
                            showCount={false}
                            className="w-full font-normal"
                            wideDropdown={true}
                            dropdownClassName="min-w-[200px]"
                        />
                    </div>
                );

            case 'date':
            case 'dateTime':
                const dateFilterValue = tempFilters[column.col_name] as CustomFieldFilter | undefined;
                const fromValue = dateFilterValue?.from || '';
                const toValue = dateFilterValue?.to || '';

                return (
                    <div className="space-y-2">
                        {/* From date input */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">From date</label>
                            <input
                                type="date"
                                value={fromValue}
                                onChange={(e) => {
                                    handleFilterChange(column.col_name, {
                                        from: e.target.value,
                                        to: toValue
                                    }, column.col_type as 'date' | 'dateTime');
                                }}
                                className="w-full px-2 py-1.5 text-sm font-normal border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-brandColor focus:border-transparent"
                            />
                        </div>

                        {/* To date input */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">To date</label>
                            <input
                                type="date"
                                value={toValue}
                                onChange={(e) => {
                                    handleFilterChange(column.col_name, {
                                        from: fromValue,
                                        to: e.target.value
                                    }, column.col_type as 'date' | 'dateTime');
                                }}
                                className="w-full px-2 py-1.5 text-sm font-normal border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-brandColor focus:border-transparent"
                                min={fromValue || undefined}
                            />
                        </div>
                    </div>
                );

            default:
                return (
                    <input
                        type="text"
                        value={currentValue}
                        onChange={(e) => handleFilterChange(column.col_name, e.target.value, 'text')}
                        placeholder={`Filter...`}
                        className="w-full px-2 py-1 text-sm font-normal border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-brandColor focus:border-transparent"
                    />
                );
        }
    };

    // Vista Form clients (Extended Profile active) hide native fields they don't use.
    const hasVistaForm = useMemo(() => columns.some((c: any) => c.is_profile), [columns]);

    // Get visible columns for rendering - include UTM/FB/LI columns that exist in data
    const displayColumns = useMemo(() => {
        // ADMIN VIEW: Use columns array directly - it's already in correct order
        if (isAdminView) {
            console.log('📊 Admin view displayColumns - using columns order directly');
            return columns.filter(col => visibleColumns.has(col.col_name));
        }

        // NORMAL VIEW: Sort by col_order ASC, phone first, end/tracking columns last

        // Get all columns from API
        const apiColumns = columns.filter(col => visibleColumns.has(col.col_name)
            && !(hasVistaForm && VISTA_UNUSED_COLUMNS.includes(col.col_name)));

        // Add UTM/FB/LI columns that aren't in API columns but are in the data
        const additionalDataColumns: Column[] = [
            { col_name: 'owner', col_label: hasVistaForm ? 'Counsellor Assigned' : 'Owner', col_type: 'text', is_custom: false },
            ...(hasVistaForm ? [] : [{ col_name: 'last_remark', col_label: 'Last Remark', col_type: 'text', is_custom: false }]),
            { col_name: 'is_qualified', col_label: 'Qualified', col_type: 'boolean', is_custom: false },
            { col_name: 'utm_source', col_label: 'UTM Source', col_type: 'text', is_custom: false },
            { col_name: 'utm_campaign', col_label: 'UTM Campaign', col_type: 'text', is_custom: false },
            { col_name: 'utm_medium', col_label: 'UTM Medium', col_type: 'text', is_custom: false },
            { col_name: 'utm_content', col_label: 'UTM Content', col_type: 'text', is_custom: false },
            { col_name: 'utm_term', col_label: 'UTM Term', col_type: 'text', is_custom: false },
            { col_name: 'utm_location', col_label: 'UTM Location', col_type: 'text', is_custom: false },
            { col_name: 'utm_device', col_label: 'UTM Device', col_type: 'text', is_custom: false },
            { col_name: 'utm_network', col_label: 'UTM Network', col_type: 'text', is_custom: false },
            { col_name: 'utm_platform', col_label: 'UTM Platform', col_type: 'text', is_custom: false },
            { col_name: 'fb_platform', col_label: 'FB Platform', col_type: 'text', is_custom: false },
            { col_name: 'fb_ad_name', col_label: 'FB Ad Name', col_type: 'text', is_custom: false },
            { col_name: 'fb_campaign_name', col_label: 'FB Campaign', col_type: 'text', is_custom: false },
            { col_name: 'li_campaign_name', col_label: 'LI Campaign', col_type: 'text', is_custom: false },
        ].filter(col => visibleColumns.has(col.col_name) && !apiColumns.find(c => c.col_name === col.col_name)
            && !(hasVistaForm && VISTA_UNUSED_COLUMNS.includes(col.col_name)));

        // Combine all columns
        const allColumns = [...apiColumns, ...additionalDataColumns];

        // Vista Form clients: Phone first, then the Vista Form's section order (same as Column
        // Visibility), with UTM/FB/LI tracking columns last.
        if (hasVistaForm) {
            const isTrackingCol = (c: Column) => c.is_tracking_field === true || /^(utm_|fb_|li_)/.test(c.col_name);
            return allColumns.sort((a, b) => {
                if (a.col_name === 'phone') return -1;
                if (b.col_name === 'phone') return 1;
                const aT = isTrackingCol(a), bT = isTrackingCol(b);
                if (aT !== bT) return aT ? 1 : -1;
                const diff = vistaColumnRank(a.col_name) - vistaColumnRank(b.col_name);
                return diff !== 0 ? diff : (a.col_label || '').localeCompare(b.col_label || '');
            });
        }

        // Columns that should appear at the end (after regular columns, before tracking)
        const endColumnNames = ['owner', 'created_date', 'response_time', 'updated_date', 'utm_source', 'utm_campaign',
            'utm_medium', 'utm_content', 'utm_term', 'utm_location', 'utm_device', 'utm_network',
            'utm_platform', 'fb_platform', 'fb_ad_name', 'fb_campaign_name', 'li_campaign_name', 'is_qualified'];
        const endColumnOrder: Record<string, number> = {
            'owner': 1, 'created_date': 2, 'response_time': 2.1, 'updated_date': 3,
            'utm_source': 4, 'utm_campaign': 5, 'utm_medium': 6, 'utm_content': 7, 'utm_term': 8,
            'utm_location': 9, 'utm_device': 10, 'utm_network': 11, 'utm_platform': 12,
            'fb_platform': 13, 'fb_ad_name': 14, 'fb_campaign_name': 15, 'li_campaign_name': 16,
            'is_qualified': 17,
        };

        // Sort by col_order ASC: phone first, regular columns by col_order, end columns last, tracking last
        return allColumns.sort((a, b) => {
            // Phone always first
            if (a.col_name === 'phone') return -1;
            if (b.col_name === 'phone') return 1;

            const aIsTracking = a.is_tracking_field === true;
            const bIsTracking = b.is_tracking_field === true;
            const aIsEnd = endColumnNames.includes(a.col_name);
            const bIsEnd = endColumnNames.includes(b.col_name);

            // Tracking fields go last
            if (!aIsTracking && bIsTracking) return -1;
            if (aIsTracking && !bIsTracking) return 1;

            // End columns go after regular columns but before tracking
            if (!aIsEnd && bIsEnd && !bIsTracking) return -1;
            if (aIsEnd && !bIsEnd && !aIsTracking) return 1;

            // Among end columns, use fixed order
            if (aIsEnd && bIsEnd) {
                return (endColumnOrder[a.col_name] || 99) - (endColumnOrder[b.col_name] || 99);
            }

            // Among tracking fields, sort by category then label
            if (aIsTracking && bIsTracking) {
                const categoryOrder: Record<string, number> = {
                    'UTM Tracking': 1, 'Facebook Tracking': 2, 'LinkedIn Tracking': 3,
                };
                if (a.category !== b.category) {
                    return (categoryOrder[a.category || ''] || 99) - (categoryOrder[b.category || ''] || 99);
                }
                return (a.col_label || '').localeCompare(b.col_label || '');
            }

            // Regular columns: sort by col_order ASC
            const aOrder = a.col_order ?? 9999;
            const bOrder = b.col_order ?? 9999;
            if (aOrder !== bOrder) return aOrder - bOrder;
            return (a.col_label || '').localeCompare(b.col_label || '');
        });
    }, [columns, visibleColumns, isAdminView, hasVistaForm]);

    // Toggle column visibility and save to database
    const toggleColumnVisibility = async (columnName: string) => {
        // Prevent toggling phone and latest_incoming_date columns - they are always required and visible
        if (columnName === 'phone' || columnName === 'latest_incoming_date') {
            console.log(`⚠️ Cannot toggle ${columnName} column - it is a required column`);
            return;
        }

        const newVisible = new Set(visibleColumns);
        if (newVisible.has(columnName)) {
            newVisible.delete(columnName);
        } else {
            newVisible.add(columnName);
        }
        setVisibleColumns(newVisible);

        // Save to both localStorage and database
        saveColumnPreferencesToStorage(newVisible);
        await saveColumnSettings(newVisible);
    };
    
    // Save column settings to database
    const saveColumnSettings = async (visibleCols: Set<string>) => {
        try {
            // Get column UUIDs for selected columns
            const selectedColumnUuids = columns
                .filter(col => visibleCols.has(col.col_name) && col.uuid)
                .map(col => col.uuid!)
                .filter(uuid => uuid !== undefined);
                
            const response = await optimizedApi.post('/user-leads-settings/columns/save', {
                selected_columns: selectedColumnUuids
            });
            
            if (response.success) {
                console.log('✅ Column settings saved successfully');
            } else {
                console.error('❌ Failed to save column settings:', response.message);
            }
        } catch (error) {
            console.error('❌ Error saving column settings:', error);
        }
    };


    // Handle bulk change owner
    const handleChangeOwner = () => {
        setIsChangeOwnerModalOpen(true);
        setIsBulkActionsOpen(false);
    };

    // Handle bulk add reminder
    const handleBulkAddReminder = useCallback(() => {
        setIsBulkReminderModalOpen(true);
        setIsBulkActionsOpen(false);
    }, []);

    // Optimized form handlers to prevent input lag
    const handleReminderTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setReminderTitle(e.target.value);
    }, []);

    const handleReminderDateTimeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setReminderDateTime(e.target.value);
    }, []);

    const handleReminderAlertChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        setReminderAlertBefore(e.target.value);
    }, []);

    const handleOwnerChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedNewOwner(e.target.value);
    }, []);

    const handleOwnerRemarkChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setOwnerChangeRemark(e.target.value);
    }, []);

    const handleCloseReminderModal = useCallback(() => {
        setIsBulkReminderModalOpen(false);
        setReminderTitle('');
        setReminderDateTime('');
        setReminderAlertBefore('15 min');
    }, []);

    const handleCloseOwnerModal = useCallback(() => {
        setIsChangeOwnerModalOpen(false);
        setSelectedNewOwner('');
        setOwnerChangeRemark('');
    }, []);

    const handleCloseDeleteModal = useCallback(() => {
        setIsDeleteConfirmModalOpen(false);
    }, []);

    // Process bulk owner change
    const processBulkOwnerChange = async () => {
        if (!selectedNewOwner || !ownerChangeRemark.trim() || selectedRows.size === 0) {
            showToast('Please select a new owner and add a remark', 'warning');
            return;
        }

        setIsProcessingBulkAction(true);
        try {
            const selectedLeadUuids = Array.from(selectedRows);

            // Use the dedicated bulk assign endpoint
            const response = await bulkAssignLeads(
                selectedLeadUuids,
                selectedNewOwner,
                ownerChangeRemark
            );

            if (response.success) {
                showToast(`Successfully updated ${selectedLeadUuids.length} lead(s).`, 'success');
                // Refresh the table data
                await fetchLeads();
                // Clear selection
                setSelectedRows(new Set());
                setSelectAll(false);
            } else {
                showToast(response.message || 'Failed to update leads. Please try again.', 'error');
            }
        } catch (error) {
            console.error('❌ Bulk owner change error:', error);
            showToast('An error occurred. Please try again.', 'error');
        } finally {
            setIsProcessingBulkAction(false);
            setIsChangeOwnerModalOpen(false);
            setSelectedNewOwner('');
            setOwnerChangeRemark('');
        }
    };

    // Process bulk add reminder
    const processBulkReminder = async () => {
        if (!reminderTitle.trim() || !reminderDateTime || selectedRows.size === 0) {
            showToast('Please fill in all required fields', 'warning');
            return;
        }

        setIsProcessingBulkAction(true);
        try {
            const selectedLeadUuids = Array.from(selectedRows);

            const response = await optimizedApi.post('/leads/reminder_lead', {
                leadUuid: selectedLeadUuids,
                event_title: reminderTitle,
                daterange: reminderDateTime,
                alert_before: reminderAlertBefore
            });

            if (response.success) {
                showToast(`Successfully added reminders to ${selectedLeadUuids.length} lead(s)`, 'success');
                // Clear selection
                setSelectedRows(new Set());
                setSelectAll(false);
            } else {
                showToast(`Failed to add reminders: ${response.message || 'Unknown error'}`, 'error');
            }
        } catch (error) {
            console.error('❌ Bulk reminder error:', error);
            showToast('An error occurred. Please try again.', 'error');
        } finally {
            setIsProcessingBulkAction(false);
            setIsBulkReminderModalOpen(false);
            setReminderTitle('');
            setReminderDateTime('');
            setReminderAlertBefore('15 min');
        }
    };

    // Handle bulk delete
    const handleBulkDelete = () => {
        setIsDeleteConfirmModalOpen(true);
        setIsBulkActionsOpen(false);
    };

    // Process bulk delete
    const processBulkDelete = async () => {
        if (selectedRows.size === 0) return;

        setIsProcessingBulkAction(true);
        try {
            const selectedLeadUuids = Array.from(selectedRows);

            const response = await optimizedApi.post('/leads/delete_lead', {
                leadUuids: selectedLeadUuids
            });

            if (response.success) {
                showToast(`Successfully deleted ${selectedLeadUuids.length} lead(s)`, 'success');
                // Refresh the table data
                await fetchLeads();
                // Clear selection
                setSelectedRows(new Set());
                setSelectAll(false);
            } else {
                showToast(`Failed to delete leads: ${response.message || 'Unknown error'}`, 'error');
            }
        } catch (error) {
            console.error('❌ Bulk delete error:', error);
            showToast('An error occurred. Please try again.', 'error');
        } finally {
            setIsProcessingBulkAction(false);
            setIsDeleteConfirmModalOpen(false);
        }
    };

    // Show loading state
    if (isLoading && leads.length === 0) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading leads...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full h-full flex flex-col">
            {/* Compact Single Line Toolbar */}
            <div className="bg-white border-b border-gray-200 shadow-sm flex-shrink-0 sticky top-0 z-50">
                <div className="px-6 py-3">
                    <div className="flex items-center justify-between">
                        {/* Left Section - Title and Stats */}
                        <div className="flex items-center gap-6">
                            <div className="flex items-center gap-3">
                                {isAdminView ? (
                                    <h1 className="text-xl font-semibold text-gray-800">{pageTitle}</h1>
                                ) : (
                                    <div data-tour="view-switcher">
                                        <LeadViewSwitcher />
                                    </div>
                                )}
                            </div>

                            {/* Bulk Actions Dropdown - Show only when items are selected */}
                            {selectedRows.size > 0 && (
                                <div className="relative" data-tour="bulk-actions-hint">
                                    <button
                                        onClick={() => setIsBulkActionsOpen(!isBulkActionsOpen)}
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
                                    >
                                        <span>Actions</span>
                                        <ChevronDown className={`h-4 w-4 transition-transform ${isBulkActionsOpen ? 'rotate-180' : ''}`} />
                                    </button>

                                    {/* Dropdown Menu */}
                                    {isBulkActionsOpen && (
                                        <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                                            <div className="py-1">
                                                <button
                                                    onClick={handleChangeOwner}
                                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors">
                                                    <UserCheck className="h-4 w-4 text-brandColor" />
                                                    Change Owner
                                                </button>
                                                <button
                                                    onClick={() => setIsExportModalOpen(true)}
                                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors">
                                                    <Download className="h-4 w-4 text-gray-600" />
                                                    Export Selected
                                                </button>
                                                <button
                                                    onClick={handleBulkAddReminder}
                                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors">
                                                    <Bell className="h-4 w-4 text-amber-600" />
                                                    Add Reminder
                                                </button>
                                                <div className="border-t border-gray-100 my-1"></div>
                                                <button
                                                    onClick={handleBulkDelete}
                                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors">
                                                    <Trash2 className="h-4 w-4" />
                                                    Delete Selected
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Right Section - Action Buttons */}
                        <div className="flex items-center gap-3">
                            {/* Admin-only filters: Is Client and Is Claimed (Toggle Switches) - Apply immediately */}
                            {isAdminView && (
                                <>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <span className="text-sm text-gray-600">Is Client</span>
                                        <div className="relative">
                                            <input
                                                type="checkbox"
                                                checked={(activeFilters['is_client'] as CustomFieldFilter | undefined)?.value === 'true'}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        const newFilter = { value: 'true', type: 'text' as const };
                                                        setTempFilters(prev => ({ ...prev, is_client: newFilter }));
                                                        setActiveFilters(prev => ({ ...prev, is_client: newFilter }));
                                                    } else {
                                                        setTempFilters(prev => {
                                                            const { is_client, ...rest } = prev;
                                                            return rest;
                                                        });
                                                        setActiveFilters(prev => {
                                                            const { is_client, ...rest } = prev;
                                                            return rest;
                                                        });
                                                    }
                                                    setPagination(prev => ({ ...prev, currentPage: 1 }));
                                                }}
                                                className="sr-only peer"
                                            />
                                            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-brandColor rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-brandColor"></div>
                                        </div>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <span className="text-sm text-gray-600">Is Claimed</span>
                                        <div className="relative">
                                            <input
                                                type="checkbox"
                                                checked={(activeFilters['is_claimed'] as CustomFieldFilter | undefined)?.value === 'true'}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        const newFilter = { value: 'true', type: 'text' as const };
                                                        setTempFilters(prev => ({ ...prev, is_claimed: newFilter }));
                                                        setActiveFilters(prev => ({ ...prev, is_claimed: newFilter }));
                                                    } else {
                                                        setTempFilters(prev => {
                                                            const { is_claimed, ...rest } = prev;
                                                            return rest;
                                                        });
                                                        setActiveFilters(prev => {
                                                            const { is_claimed, ...rest } = prev;
                                                            return rest;
                                                        });
                                                    }
                                                    setPagination(prev => ({ ...prev, currentPage: 1 }));
                                                }}
                                                className="sr-only peer"
                                            />
                                            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-brandColor rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-brandColor"></div>
                                        </div>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer" title="Show only leads with AI call summaries">
                                        <Bot className="h-4 w-4 text-purple-600" />
                                        <span className="text-sm text-gray-600">AI Summary</span>
                                        <div className="relative">
                                            <input
                                                type="checkbox"
                                                checked={(activeFilters['hasAiSummary'] as CustomFieldFilter | undefined)?.value === 'true'}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        const newFilter = { value: 'true', type: 'text' as const };
                                                        setTempFilters(prev => ({ ...prev, hasAiSummary: newFilter }));
                                                        setActiveFilters(prev => ({ ...prev, hasAiSummary: newFilter }));
                                                    } else {
                                                        setTempFilters(prev => {
                                                            const { hasAiSummary, ...rest } = prev;
                                                            return rest;
                                                        });
                                                        setActiveFilters(prev => {
                                                            const { hasAiSummary, ...rest } = prev;
                                                            return rest;
                                                        });
                                                    }
                                                    setPagination(prev => ({ ...prev, currentPage: 1 }));
                                                }}
                                                className="sr-only peer"
                                            />
                                            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-purple-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-600"></div>
                                        </div>
                                    </label>
                                </>
                            )}
                            {/* 1. Owner Filter - Hidden for Sales Reps and Admin View */}
                            {!isSalesRep && !isAdminView && (
                                <div className="flex items-center gap-2" data-tour="owner-filter">
                                    <Users className="h-4 w-4 text-gray-500" />
                                    <FSelect
                                        options={[
                                            { uuid: 'unassigned', opt_id: 'unassigned', opt_name: 'Unassigned' },
                                            ...users.map(user => ({
                                                uuid: user.uuid,
                                                opt_id: user.uuid,
                                                opt_name: user.userName
                                            }))
                                        ]}
                                        value={Array.isArray((tempFilters['owner'] as CustomFieldFilter | undefined)?.value) ? (tempFilters['owner'] as CustomFieldFilter).value : []}
                                        onChange={(selectedValues) => handleFilterChange('owner', selectedValues, 'select')}
                                        placeholder="All Owners"
                                        searchable={true}
                                        showCount={false}
                                        className="min-w-[200px]"
                                        wideDropdown={true}
                                        dropdownClassName="min-w-[250px]"
                                    />
                                </div>
                            )}

                            {/* 2. Apply Button - Hidden for Admin View */}
                            {!isAdminView && (
                                <button
                                    data-tour="apply-filters"
                                    onClick={applyFilters}
                                    disabled={!hasFiltersToApply}
                                    className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition-all ${
                                        hasFiltersToApply
                                            ? 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                                            : 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
                                    }`}
                                    title="Apply Filters"
                                >
                                    <Check className="h-4 w-4" />
                                    <span className="hidden md:inline">Apply</span>
                                    {Object.keys(tempFilters).length > 0 && (
                                        <span className="px-2 py-0.5 text-xs bg-brandColor text-white rounded-full font-bold">
                                            {Object.keys(tempFilters).length}
                                        </span>
                                    )}
                                </button>
                            )}

                            {/* 3. Clear Button - Hidden for Admin View */}
                            {!isAdminView && Object.keys(activeFilters).length > 0 && (
                                <button
                                    onClick={clearFilters}
                                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all"
                                    title="Clear all filters"
                                >
                                    <X className="h-4 w-4" />
                                    <span className="hidden md:inline">Clear</span>
                                </button>
                            )}

                            <div className="h-6 w-px bg-gray-300"></div>

                            {/* 3. Search Component */}
                            <div data-tour="search">
                                <PipelineSearch
                                    onSearch={handleSearch}
                                    isLoading={isLoading}
                                    placeholder="Search leads by name, email, or phone..."
                                    currentSearchTerm={searchTerm}
                                    className="flex-shrink-0"
                                />
                            </div>

                            <div className="h-6 w-px bg-gray-300"></div>

                            {/* 4. Advanced Filters */}
                            <button
                                data-tour="advanced-filters"
                                onClick={() => setIsAdvancedFiltersOpen(true)}
                                className="inline-flex items-center justify-center p-2 bg-white text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all"
                                title="Advanced Filters"
                            >
                                <Filter className="h-4 w-4" />
                            </button>

                            {/* 5. Add Lead */}
                            <button
                                data-tour="add-lead"
                                onClick={() => setIsAddLeadModalOpen(true)}
                                className="inline-flex items-center justify-center p-2 bg-brandColor text-white rounded-lg hover:bg-brand-hover transition-all hover:shadow-md"
                                title="Add Lead"
                            >
                                <Plus className="h-5 w-5" />
                            </button>

                            {/* 6. Export */}
                            <button
                                data-tour="export"
                                onClick={() => setIsExportModalOpen(true)}
                                className="inline-flex items-center justify-center p-2 bg-white text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all"
                                title="Export Leads"
                            >
                                <Download className="h-5 w-5" />
                            </button>

                            {/* Column Settings */}
                            <div className="flex items-center" data-tour="column-settings">
                                <ColumnVisibilityDropdown
                                    columns={columns}
                                    visibleColumns={visibleColumns}
                                    onToggleColumn={toggleColumnVisibility}
                                    iconOnly={true}
                                    vistaForm={hasVistaForm}
                                />
                            </div>

                            {/* Tour Button */}
                            {tourButton && tourButton}
                        </div>
                    </div>

                    {/* Active Filters Row - Compact */}
                    {(Object.keys(activeFilters).length > 0 || searchTerm) && (
                        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-200">
                            <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Active Filters:</span>
                            <div className="flex flex-wrap gap-2">
                                {/* Search Filter */}
                                {searchTerm && (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs bg-brandColor/5 text-gray-700 rounded-full border border-brandColor/20">
                                        <span className="font-medium">Search:</span>
                                        <span className="text-brandColor" title={searchTerm}>
                                            {searchTerm.length > 20 ? searchTerm.substring(0, 20) + '...' : searchTerm}
                                        </span>
                                        <button
                                            onClick={() => handleSearch('')}
                                            className="ml-1 text-gray-400 hover:text-red-600 transition-colors"
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </span>
                                )}
                                {Object.entries(activeFilters).map(([key, filterItem]) => {
                                    const filter = filterItem as CustomFieldFilter;
                                    if (!filter) return null;

                                    // Skip empty filters
                                    if (filter.type === 'date' || filter.type === 'dateTime') {
                                        // For date filters, check if from or to exists
                                        if (!filter.from && !filter.to) return null;
                                    } else {
                                        // For other filters, check if value exists
                                        if (!filter.value) return null;
                                    }

                                    // Get display label and value
                                    let displayLabel = key === 'owner' ? 'Owner' : columns.find(col => col.col_name === key)?.col_label || key;
                                    let displayValue = '';
                                    let titleValue = '';

                                    // Handle special filter keys
                                    if (key === 'hasAiSummary') {
                                        displayLabel = 'AI Summary';
                                        displayValue = 'Yes';
                                        titleValue = 'Leads with AI call summaries';
                                    }
                                    else if (key === 'is_client') {
                                        displayLabel = 'Is Client';
                                        displayValue = 'Yes';
                                        titleValue = 'Active clients only';
                                    }
                                    else if (key === 'is_claimed') {
                                        displayLabel = 'Is Claimed';
                                        displayValue = 'Yes';
                                        titleValue = 'Claimed schools only';
                                    }
                                    // Handle date filters specially
                                    else
                                    if (filter.type === 'date' || filter.type === 'dateTime') {
                                        const parts = [];
                                        if (filter.from) parts.push(`From ${filter.from}`);
                                        if (filter.to) parts.push(`To ${filter.to}`);
                                        displayValue = parts.join(' ');
                                        titleValue = displayValue;
                                    }
                                    // Handle owner filter
                                    else if (key === 'owner' && Array.isArray(filter.value)) {
                                        const selectedOwnerNames = filter.value.map((uuid: any) => {
                                            if (uuid === 'unassigned') return 'Unassigned';
                                            const user = users.find(u => u.uuid === uuid);
                                            return user ? user.userName : uuid;
                                        });
                                        displayValue = selectedOwnerNames.length === 1
                                            ? selectedOwnerNames[0]
                                            : `${selectedOwnerNames.length} selected`;
                                        titleValue = selectedOwnerNames.join(', ');
                                    }
                                    // Handle other array filters
                                    else if (Array.isArray(filter.value)) {
                                        displayValue = `${filter.value.length} selected`;
                                        titleValue = filter.value.join(', ');
                                    }
                                    // Handle text filters
                                    else {
                                        displayValue = String(filter.value).substring(0, 15);
                                        titleValue = String(filter.value);
                                    }

                                    return (
                                        <span
                                            key={key}
                                            className="inline-flex items-center gap-1.5 px-3 py-1 text-xs bg-brandColor/5 text-gray-700 rounded-full border border-brandColor/20"
                                        >
                                            <span className="font-medium">
                                                {displayLabel}:
                                            </span>
                                            <span className="text-brandColor" title={titleValue}>
                                                {displayValue}
                                            </span>
                                            <button
                                                onClick={() => {
                                                    const newFilters = { ...activeFilters };
                                                    delete newFilters[key];
                                                    setActiveFilters(newFilters);
                                                    const newTempFilters = { ...tempFilters };
                                                    delete newTempFilters[key];
                                                    setTempFilters(newTempFilters);
                                                }}
                                                className="ml-1 text-gray-400 hover:text-red-600 transition-colors"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Table Container Wrapper with Padding */}
            <div className="px-6 flex-1 flex flex-col min-h-0">
                {/* Table Container */}
                <div
                    ref={tableContainerRef}
                    className="mt-4 bg-white border border-gray-200 rounded-lg shadow-sm overflow-auto flex-1 min-h-0"
                >
                <table className="w-full border-collapse">
                    {/* Header Row */}
                    <thead
                        ref={headerRef}
                        className="sticky top-0 bg-brandColor border-b border-brandColor z-20"
                    >
                        <tr className="border-b border-white/20">
                            {/* Fixed Checkbox Column */}
                            <th
                                className="sticky left-0 bg-brandColor p-1.5 text-center border-r border-white/20 z-30 shadow-md"
                                style={{ width: '50px', minWidth: '50px', maxWidth: '50px' }}
                                data-tour="select-all"
                            >
                                <input
                                    type="checkbox"
                                    checked={selectAll}
                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                    className="w-4 h-4 text-brandColor border-gray-300 rounded focus:ring-brandColor"
                                />
                            </th>

                            {/* Dynamic Columns */}
                            {displayColumns.map((column, colIndex) => {
                                const canFilter = column.col_name !== 'owner' && column.col_name !== 'last_remark' && column.col_name !== 'admin_last_remark' && !(isAdminView && column.col_name === 'status');
                                const isFiltered = hasActiveFilter(column.col_name);
                                const isFirstColumn = colIndex === 0;
                                const isFirstFilterableColumn = canFilter && displayColumns.slice(0, colIndex).every(c => c.col_name === 'owner' || c.col_name === 'last_remark' || c.col_name === 'admin_last_remark');

                                return (
                                    <th
                                        key={column.col_name}
                                        className="px-2 py-1.5 text-left font-medium text-sm text-white border-r border-white/20"
                                        style={{ minWidth: '175px', maxWidth: '250px' }}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <div
                                                className="flex items-center gap-1 cursor-pointer hover:text-white/80 flex-1 min-w-0"
                                                onClick={() => handleSort(column.col_name)}
                                                data-tour={isFirstColumn ? "column-header" : undefined}
                                                title={column.col_label}
                                            >
                                                <span className="truncate">{column.col_label}</span>
                                                {sortColumn === column.col_name && (
                                                    <span className="text-sm flex-shrink-0">{sortDirection === 'ASC' ? '↑' : '↓'}</span>
                                                )}
                                            </div>

                                            {canFilter && (
                                                <button
                                                    onClick={(e) => handleFilterIconClick(column.col_name, e)}
                                                    className={`p-1 rounded transition-colors ${
                                                        isFiltered
                                                            ? 'bg-orange-500 text-white hover:bg-orange-600'
                                                            : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200'
                                                    }`}
                                                    title={isFiltered ? "Filter active - click to modify" : "Filter column"}
                                                    data-tour={isFirstFilterableColumn ? "column-filter" : undefined}
                                                >
                                                    <Filter className="h-3.5 w-3.5" />
                                                </button>
                                            )}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>

                    {/* Table Body */}
                    <tbody>
                        {leads.map((lead, rowIndex) => (
                            <tr
                                key={lead.uuid}
                                className={cn(
                                    "border-b border-gray-100 hover:bg-gray-50 transition-colors",
                                    selectedRows.has(lead.uuid) && "bg-brandColor/5",
                                    rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50"
                                )}
                                data-tour={rowIndex === 0 ? "table-row" : undefined}
                            >
                                {/* Fixed Checkbox Cell */}
                                <td
                                    className="sticky left-0 p-1.5 text-center border-r border-gray-300 bg-white z-10 shadow-md"
                                    style={{ width: '50px', minWidth: '50px', maxWidth: '50px' }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedRows.has(lead.uuid)}
                                        onChange={(e) => handleRowSelect(lead.uuid, e.target.checked)}
                                        className="w-3.5 h-3.5 text-brandColor border-gray-300 rounded focus:ring-brandColor"
                                    />
                                </td>

                                {/* Dynamic Data Cells */}
                                {displayColumns.map((column) => {
                                    const isClickable = ['phone'].includes(column.col_name);
                                    const isPhoneColumn = column.col_name === 'phone';

                                    // Phone column with special rendering
                                    if (isPhoneColumn) {
                                        // Handle both string and number types for duplicate_count
                                        const duplicateCount = lead.duplicate_count ? Number(lead.duplicate_count) : 0;
                                        const hasDuplicates = duplicateCount > 0;
                                        const hasEvents = lead.events && Array.isArray(lead.events) && lead.events.length > 0;
                                        const hasAiSummary = lead.hasAiSummary === true;
                                        const phoneNumber = getCellValue(lead, column.col_name);
                                        const hasPhone = phoneNumber && phoneNumber.trim() !== '';
                                        const canCall = outboundConfig && hasPhone && !isCallInProgress;

                                        return (
                                            <td
                                                key={column.col_name}
                                                className="px-2 py-1.5 border-r border-gray-200 text-sm text-gray-900 cursor-pointer hover:text-brandColor font-medium"
                                                style={{ minWidth: '200px', maxWidth: '280px' }}
                                                title={phoneNumber}
                                                onClick={() => onLeadClick?.(lead)}
                                            >
                                                <div className="flex items-center gap-2">
                                                    {/* Phone number - left side */}
                                                    <span className="truncate flex-1">
                                                        {formatPhoneDisplay(phoneNumber, lead.country_code)}
                                                    </span>

                                                    {/* Middle badges/icons */}
                                                    <div className="flex items-center gap-2 flex-shrink-0">
                                                        {/* Calendar icon if events exist */}
                                                        {hasEvents && (
                                                            <Calendar
                                                                className="h-4 w-4 text-brandColor"
                                                                aria-label={`${lead.events.length} event(s)`}
                                                            />
                                                        )}

                                                        {/* AI Summary icon if lead has Echo Leads call summary */}
                                                        {hasAiSummary && (
                                                            <span
                                                                className="inline-flex items-center justify-center w-5 h-5 text-purple-700 bg-purple-100 rounded-full"
                                                                title="AI Call Summary Available"
                                                            >
                                                                <Bot className="h-3 w-3" />
                                                            </span>
                                                        )}

                                                        {/* Duplicate count badge - only if > 0 */}
                                                        {hasDuplicates && (
                                                            <span
                                                                className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-semibold text-orange-700 bg-orange-100 rounded-full"
                                                                title={`${duplicateCount} duplicate(s)`}
                                                            >
                                                                <Copy className="h-3 w-3" />
                                                                {duplicateCount}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* Phone icon - only show if calling is configured */}
                                                    {canCall && (
                                                        <button
                                                            onClick={(e) => handleInitiateCall(lead, e)}
                                                            className="text-green-600 hover:text-green-700 hover:bg-green-50 rounded p-1 transition-colors flex-shrink-0"
                                                            title="Click to call"
                                                            disabled={isCallInProgress}
                                                        >
                                                            <Phone className="h-4 w-4" />
                                                        </button>
                                                    )}

                                                    {/* Runo click-to-call - only in admin view when Runo is active and current user has a Runo phone configured */}
                                                    {isAdminView && runoActive && runoUserEnabled && hasPhone && (
                                                        <button
                                                            onClick={(e) => handleRunoClickToCall(lead, e)}
                                                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded p-1 transition-colors flex-shrink-0"
                                                            title="Call via Runo"
                                                            disabled={runoCallingLeadId === lead.uuid}
                                                        >
                                                            {runoCallingLeadId === lead.uuid
                                                                ? <Loader2 className="h-4 w-4 animate-spin" />
                                                                : <PhoneCall className="h-4 w-4" />
                                                            }
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        );
                                    }

                                    // School Name column - show "Attach School" link when N/A in admin view
                                    if (isAdminView && column.col_name === 'clientName') {
                                        const schoolName = getCellValue(lead, column.col_name);
                                        const hasNoSchool = !schoolName || schoolName === 'N/A';

                                        return (
                                            <td
                                                key={column.col_name}
                                                className={`px-2 py-1.5 border-r border-gray-200 text-sm ${hasNoSchool ? 'bg-orange-50' : ''}`}
                                                style={{ minWidth: '175px', maxWidth: '250px' }}
                                            >
                                                {hasNoSchool ? (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onAttachSchool?.(lead);
                                                        }}
                                                        className="text-orange-600 hover:text-orange-700 hover:underline font-medium"
                                                    >
                                                        + Attach School
                                                    </button>
                                                ) : (
                                                    <div className="truncate text-gray-900" title={schoolName}>
                                                        {schoolName}
                                                    </div>
                                                )}
                                            </td>
                                        );
                                    }

                                    // Regular columns
                                    return (
                                        <td
                                            key={column.col_name}
                                            className={`px-2 py-1.5 border-r border-gray-200 text-sm text-gray-900 ${isClickable ? 'cursor-pointer hover:text-brandColor font-medium' : ''}`}
                                            style={{ minWidth: '175px', maxWidth: '250px' }}
                                            title={getCellValue(lead, column.col_name)}
                                            onClick={isClickable ? () => onLeadClick?.(lead) : undefined}
                                        >
                                            <div className="truncate">
                                                {getCellValue(lead, column.col_name)}
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* Empty State */}
                {leads.length === 0 && !isLoading && (
                    <div className="flex flex-col items-center justify-center py-16">
                        <div className="text-gray-400 mb-4">
                            <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                            </svg>
                        </div>
                        <p className="text-gray-500 font-medium">No leads found</p>
                        <p className="text-gray-400 text-sm mt-1">Try adjusting your filters or add a new lead</p>
                    </div>
                )}

                {/* Loading Overlay */}
                {isLoading && (
                    <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-40 rounded-lg">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-10 w-10 border-3 border-brandColor border-t-transparent mx-auto mb-3"></div>
                            <p className="text-gray-600 font-medium">Loading leads...</p>
                        </div>
                    </div>
                )}

                {/* Filter Popover */}
                {filterPopoverColumn && filterPopoverPosition && (
                    <>
                        {/* Backdrop */}
                        <div
                            className="fixed inset-0 z-40"
                            onClick={closeFilterPopover}
                        />
                        {/* Popover */}
                        <div
                            className="fixed bg-white border border-gray-200 rounded-lg shadow-xl p-4 z-50"
                            style={{
                                top: `${filterPopoverPosition.top}px`,
                                left: `${filterPopoverPosition.left}px`,
                                minWidth: '280px',
                                maxWidth: '320px'
                            }}
                        >
                            <div className="mb-3 flex items-center justify-between">
                                <h4 className="font-semibold text-sm text-gray-900">
                                    Filter: {columns.find(c => c.col_name === filterPopoverColumn)?.col_label || filterPopoverColumn}
                                </h4>
                                <button
                                    onClick={closeFilterPopover}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>

                            {/* Apply/Clear buttons at top so they're always visible */}
                            <div className="mb-3 flex gap-2">
                                <button
                                    onClick={() => {
                                        applyFilters();
                                        closeFilterPopover();
                                    }}
                                    className="flex-1 px-3 py-1.5 text-sm bg-brandColor text-white rounded hover:bg-brand-hover transition-colors font-medium"
                                >
                                    Apply
                                </button>
                                <button
                                    onClick={() => {
                                        if (filterPopoverColumn) {
                                            // Clear this column's filter
                                            const newFilters = { ...tempFilters };
                                            delete newFilters[filterPopoverColumn];
                                            setTempFilters(newFilters);

                                            const newActiveFilters = { ...activeFilters };
                                            delete newActiveFilters[filterPopoverColumn];
                                            setActiveFilters(newActiveFilters);
                                        }
                                        closeFilterPopover();
                                    }}
                                    className="flex-1 px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors font-medium"
                                >
                                    Clear
                                </button>
                            </div>

                            <div className="space-y-3">
                                {(() => {
                                    const column = columns.find(c => c.col_name === filterPopoverColumn);
                                    if (!column) return null;

                                    return renderFilterInput(column);
                                })()}
                            </div>

                        </div>
                    </>
                )}
            </div>

            {/* Pagination and Stats */}
            <div className="bg-white border border-gray-200 rounded-lg mt-2 px-4 py-2 shadow-sm flex-shrink-0">
                <div className="flex items-center justify-between">
                    {/* Results Stats */}
                    <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600">
                            Showing <span className="font-semibold text-gray-900">{((pagination.currentPage - 1) * pagination.itemsPerPage) + 1}</span> to{' '}
                            <span className="font-semibold text-gray-900">{Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}</span> of{' '}
                            <span className="font-semibold text-gray-900">{pagination.totalItems}</span> results
                        </span>
                        {selectedRows.size > 0 && (
                            <span className="px-2 py-0.5 text-xs bg-brandColor text-white rounded font-medium">
                                {selectedRows.size} selected
                            </span>
                        )}
                    </div>

                    {/* Pagination Controls */}
                    <div className="flex items-center gap-3">
                        {/* Items per page */}
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">Show:</span>
                            <select
                                value={pagination.itemsPerPage}
                                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                                className="px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-brandColor focus:border-transparent bg-white"
                            >
                                {[10, 15, 25, 50, 100].map(size => (
                                    <option key={size} value={size}>{size}</option>
                                ))}
                            </select>
                        </div>

                        {/* Page Navigation */}
                        <div className="flex items-center gap-1">
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handlePageChange(1)}
                                disabled={pagination.currentPage === 1}
                                leftIcon={<ChevronsLeft className="h-3.5 w-3.5" />}
                            />
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handlePageChange(pagination.currentPage - 1)}
                                disabled={pagination.currentPage === 1}
                                leftIcon={<ChevronLeft className="h-3.5 w-3.5" />}
                            />

                            <span className="px-3 py-1 text-sm bg-gray-50 rounded border border-gray-200">
                                <span className="text-gray-600">Page</span>
                                <span className="font-semibold text-gray-900 mx-1">{pagination.currentPage}</span>
                                <span className="text-gray-600">of {pagination.totalPages}</span>
                            </span>

                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handlePageChange(pagination.currentPage + 1)}
                                disabled={pagination.currentPage === pagination.totalPages}
                                rightIcon={<ChevronRight className="h-3.5 w-3.5" />}
                            />
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handlePageChange(pagination.totalPages)}
                                disabled={pagination.currentPage === pagination.totalPages}
                                rightIcon={<ChevronsRight className="h-3.5 w-3.5" />}
                            />
                        </div>
                    </div>
                </div>
            </div>
            </div>

            {/* Change Owner Modal */}
            {isChangeOwnerModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                        <h3 className="text-lg font-semibold mb-4">Change Owner</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Select New Owner
                                </label>
                                <select
                                    value={selectedNewOwner}
                                    onChange={handleOwnerChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent"
                                >
                                    <option value="">Select a user...</option>
                                    {users.map((user) => (
                                        <option key={user.uuid} value={user.uuid}>
                                            {user.userName} ({user.userEmail})
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Remark (Required)
                                </label>
                                <textarea
                                    value={ownerChangeRemark}
                                    onChange={handleOwnerRemarkChange}
                                    rows={3}
                                    placeholder="Enter reason for owner change..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent resize-none"
                                />
                            </div>
                            <p className="text-sm text-gray-600">
                                {selectedRows.size} lead(s) will be assigned to the new owner.
                            </p>
                        </div>
                        <div className="flex gap-3 mt-6">
                            <Button
                                variant="outline"
                                onClick={handleCloseOwnerModal}
                                disabled={isProcessingBulkAction}
                                className="flex-1"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={processBulkOwnerChange}
                                disabled={!selectedNewOwner || !ownerChangeRemark.trim() || isProcessingBulkAction}
                                className="flex-1"
                            >
                                {isProcessingBulkAction ? 'Processing...' : 'Change Owner'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Reminder Modal */}
            {isBulkReminderModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                        <h3 className="text-lg font-semibold mb-4">Add Reminder to {selectedRows.size} Lead(s)</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Reminder Title *
                                </label>
                                <input
                                    type="text"
                                    value={reminderTitle}
                                    onChange={handleReminderTitleChange}
                                    placeholder="Enter reminder title..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Date & Time *
                                </label>
                                <input
                                    type="datetime-local"
                                    value={reminderDateTime}
                                    onChange={handleReminderDateTimeChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Alert Before
                                </label>
                                <select
                                    value={reminderAlertBefore}
                                    onChange={handleReminderAlertChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent"
                                >
                                    <option value="15 min">15 min</option>
                                    <option value="30 min">30 min</option>
                                    <option value="1 hour">1 hour</option>
                                    <option value="2 hours">2 hours</option>
                                    <option value="1 day">1 day</option>
                                    <option value="2 days">2 days</option>
                                </select>
                            </div>
                            <p className="text-sm text-gray-600">
                                A reminder will be added to {selectedRows.size} selected lead(s).
                            </p>
                        </div>
                        <div className="flex gap-3 mt-6">
                            <Button
                                variant="outline"
                                onClick={handleCloseReminderModal}
                                disabled={isProcessingBulkAction}
                                className="flex-1"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={processBulkReminder}
                                disabled={!reminderTitle.trim() || !reminderDateTime || isProcessingBulkAction}
                                className="flex-1"
                            >
                                {isProcessingBulkAction ? 'Adding...' : 'Add Reminder'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {isDeleteConfirmModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                        <h3 className="text-lg font-semibold mb-4 text-red-600">Confirm Delete</h3>
                        <p className="text-gray-700 mb-4">
                            Are you sure you want to delete {selectedRows.size} selected lead(s)? This action cannot be undone.
                        </p>
                        <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                            <p className="text-sm text-red-800">
                                ⚠️ This will permanently remove all selected leads and their associated data.
                            </p>
                        </div>
                        <div className="flex gap-3 mt-6">
                            <Button
                                variant="outline"
                                onClick={handleCloseDeleteModal}
                                disabled={isProcessingBulkAction}
                                className="flex-1"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={processBulkDelete}
                                disabled={isProcessingBulkAction}
                                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                            >
                                {isProcessingBulkAction ? 'Deleting...' : 'Delete All'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Lead Modal */}
            <AddLeadModal
                isOpen={isAddLeadModalOpen}
                onClose={() => setIsAddLeadModalOpen(false)}
                refreshData={fetchLeads}
                onLeadCreated={(lead) => onLeadClick?.(lead)}
            />

            {/* Advanced Filters Modal */}
            <PipelineFiltersEnhanced
                isOpen={isAdvancedFiltersOpen}
                onClose={() => setIsAdvancedFiltersOpen(false)}
                onApplyFilters={handleAdvancedFiltersApply}
                columns={columns.map(col => ({
                    col_id: col.uuid || col.col_name,
                    col_name: col.col_name,
                    col_label: col.col_label,
                    col_type: col.col_type,
                    col_required: false,
                    col_order: col.col_order,
                    options: col.options
                }))}
                currentFilters={convertLeadsFiltersToPipelineFormat(activeFilters)}
                isLoading={isLoading}
                {...(columns.some((c: any) => c.is_profile) ? {
                    additionalFilterOrder: PROFILE_ADDITIONAL_FILTER_ORDER,
                    textFilterLabels: { phone: 'Contact Phone', name: 'Contact Name', email: 'Contact Email' },
                    extraDateFilters: columns.some((c) => c.col_name === 'next_followup_date')
                        ? [{ key: 'next_followup_date', label: PROFILE_FIELD_LABELS.next_followup_date }]
                        : [],
                } : {})}
            />

            {/* Export Modal */}
            <ExportModalWithFieldSelection
                isOpen={isExportModalOpen}
                onClose={() => setIsExportModalOpen(false)}
                selectedRows={Array.from(selectedRows)}
                totalFilteredCount={pagination.totalItems}
                currentFilters={{
                    primary_filters: activeFilters,
                    custom_filters: {}
                }}
                apiEndpoint={apiEndpoint}
            />

            {/* Initiate Call Modal */}
            {isCallModalOpen && selectedCallLead && (
                <InitiateCallModal
                    isOpen={isCallModalOpen}
                    onClose={handleCloseCallModal}
                    leadUuid={selectedCallLead.uuid}
                    leadName={selectedCallLead.name}
                    leadPhone={formatPhoneForTel(selectedCallLead.phone, selectedCallLead.country_code)}
                />
            )}
        </div>
    );
});

EnhancedLeadsTable.displayName = 'EnhancedLeadsTable';

export default EnhancedLeadsTable;